/**
 * Overview (#/overview) — AI Security Command Center shell (glassmorphism layout).
 * Visual layer; fleet KPIs bind to real overview poll where noted.
 */

import { escapeHtml, setChildMarkup } from "./dom.js";
import { humanizeAlarmTitle, humanizeZoneLabel, humanizeProofReason, softDeviceLabel, softFleetDeviceTitle, isHarnessDeviceId, fmtRel, fmtDeviceCount, fmtDevicesOnlineOfTotal, softOperatorNote } from "./format.js";
import { NAV_GROUPS, ROLE_WEIGHT } from "./constants.js";
import { canUseOpsMap } from "./ops-map-policy.js";
import { applyHomeLandingChrome, getHomeLandingPreference } from "./home-priority.js";
import { catalogEnabled, catalogAnyEnabled } from "./capability-gate.js";
import { setCommandCenterMap3d, setCommandCenterMapNight } from "./dashboard/map/CommandMap.js";
import {
  BRAND_PRODUCT,
  _BRAND_SERIES,
  BRAND_WORDMARK,
  BRAND_TAGLINE,
  BRAND_LOGO_SRC,
  BRAND_LOGO_ALT,
} from "./brand.js";
import { maicFieldsFromAiItems } from "./response-loop-workbench.js";
import { buildMapAiAlertStageHtml, patchMapAiAlertStage, setAiMapModalOpen } from "./map-ai-alert-stage.js";
import { decisionSurfaceActionsHtml } from "./decision-surface.js";
import { buildAiContextDeepHtml } from "./ai-context-deep.js";
import { buildTonightWatchHtml } from "./tonight-watch-strip.js";
import { softLanesMapChipHtml } from "./capability-registry-facade.js";

/** Wire hash only — never paint it. Home / CC chrome use data-cc-go. */
export function hashFromCcGo(dest, extra) {
  const path = String(dest || "").replace(/^#\/?/, "").replace(/^\/+/, "");
  const id = String(extra || "").trim();
  if (!path) return "";
  if (path.includes("?") || path.includes("/")) return `#/${path}`;
  if (path === "incidents" && id) return `#/incidents/${encodeURIComponent(id)}`;
  if (path === "devices" && id) return `#/devices/${encodeURIComponent(id)}`;
  if (path === "response" && id) return `#/response?tab=${encodeURIComponent(id)}`;
  if (path === "auto-sentinel" && id) return `#/auto-sentinel?id=${encodeURIComponent(id)}`;
  if (path === "snapshots" && id) return `#/snapshots?id=${encodeURIComponent(id)}`;
  if (path === "orchestration" && id) return `#/orchestration?event_id=${encodeURIComponent(id)}`;
  return `#/${path}`;
}

/** One document listener — Settings / Policy / Home all use data-cc-go. */
export function ensureCcGoDocumentWire() {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (root.dataset.ccGoDoc === "1") return;
  root.dataset.ccGoDoc = "1";
  document.addEventListener("click", (ev) => {
    const btn = ev.target && ev.target.closest ? ev.target.closest("[data-cc-go]") : null;
    if (!btn) return;
    ev.preventDefault();
    const dest = String(btn.getAttribute("data-cc-go") || "").trim();
    if (dest === "logout") {
      const real = document.getElementById("logoutBtn");
      if (real) real.click();
      return;
    }
    const hash = hashFromCcGo(dest, btn.getAttribute("data-cc-id"));
    if (!hash) return;
    try { location.hash = hash; } catch (_) {}
  });
}

export function wireCcGoClicks(root) {
  ensureCcGoDocumentWire();
  if (root && root.dataset) root.dataset.ccGoWired = "1";
}

if (typeof document !== "undefined") {
  try { ensureCcGoDocumentWire(); } catch (_) {}
}

function ccGoBtn(dest, label, cls, extra) {
  const id = extra != null && extra !== "" ? ` data-cc-id="${escapeHtml(String(extra))}"` : "";
  return `<button type="button" class="${cls}" data-cc-go="${escapeHtml(dest)}"${id}>${label}</button>`;
}

const CC_NAV_LABELS = {
  Home: "Home",
  Operations: "Operations",
  Assets: "Assets",
  Administration: "Admin",
};

const CC_ZONE_COLORS = ["blue", "purple", "green", "orange", "yellow"];

function initials(name) {
  return String(name || "?")
    .split(/\s+/)
    .map((p) => p[0] || "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function deviceOnline(d) {
  try {
    if (typeof isOverviewDeviceOnline === "function") return isOverviewDeviceOnline(d);
  } catch (_) {}
  if (typeof deviceReachable === "function") return deviceReachable(d);
  return !!(d && (d.online || d.reachable));
}

function deriveZoneCards(devices) {
  const byGroup = new Map();
  for (const d of Array.isArray(devices) ? devices : []) {
    const nameRaw = String(d.notification_group || d.zone || "").trim();
    const soft = humanizeZoneLabel(nameRaw);
    const name = soft || "Unassigned";
    const key = name.toLowerCase();
    if (!byGroup.has(key)) byGroup.set(key, { id: key, name, total: 0, online: 0 });
    const z = byGroup.get(key);
    z.total += 1;
    if (deviceOnline(d)) z.online += 1;
  }
  return Array.from(byGroup.values()).slice(0, 8);
}

function buildZoneRows(devices) {
  const zones = deriveZoneCards(devices);
  if (!zones.length) {
    return `<p class="muted croc-cc-empty">No zones yet — claim a real unit to fill the map. Practice soak devices stay off Home so this list stays usable.</p>`;
  }
  return zones.map((z, i) => {
    const color = CC_ZONE_COLORS[i % CC_ZONE_COLORS.length];
    return `
      <div class="croc-cc-zone croc-cc-zone--card croc-cc-zone--${color} glass-panel glass-panel--micro" data-cc-zone="${escapeHtml(z.id)}" role="group" aria-label="${escapeHtml(z.name)}">
        <span class="croc-cc-zone__glow" aria-hidden="true"></span>
        <span class="croc-cc-zone__icon" aria-hidden="true">⬡</span>
        <span class="croc-cc-zone__body">
          <span class="croc-cc-zone__name">${escapeHtml(z.name)}</span>
          <span class="croc-cc-zone__meta">${fmtDeviceCount(z.total)} · ${z.online} online</span>
        </span>
        <span class="croc-cc-zone__dot croc-cc-zone__dot--ok" title="Online"></span>
      </div>`;
  }).join("");
}

function buildAssetRows(devices) {
  const all = Array.isArray(devices) ? devices : [];
  const seen = new Set();
  const primary = [];
  const harness = [];
  for (const d of all) {
    const id = String((d && d.device_id) || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (isHarnessDeviceId(id)) harness.push(d);
    else primary.push(d);
  }
  // Prefer real fleet units on Home; harness only fills if nothing else.
  const unique = primary.length ? primary : harness;
  const list = unique.slice(0, 12);
  if (!list.length) {
    return `<div class="croc-cc-empty-desk">
      <p class="muted croc-cc-empty">No operational devices in this view. Practice soak devices stay off Home so the list stays usable.</p>
      <div class="row" style="gap:8px;flex-wrap:wrap;margin-top:8px">
        <button type="button" class="btn sm secondary btn-tap" id="crocCcShowPracticeHome">Show practice on Home</button>
        ${ccGoBtn("group/_all", "All boards", "btn sm ghost btn-tap")}
      </div>
    </div>`;
  }
  const rows = list.map((d) => {
    const id = String(d.device_id || "").trim();
    const on = deviceOnline(d);
    const locRaw = humanizeZoneLabel(d.location_label || d.notification_group || d.zone) || "";
    const name = softFleetDeviceTitle(d);
    // Skip loc when empty, same as title, or generic filler that reads as jargon.
    const loc =
      !locRaw || locRaw === name || /^site$/i.test(locRaw) || /^ungrouped$/i.test(locRaw) || /^unassigned$/i.test(locRaw)
        ? ""
        : locRaw;
    return `
      <button type="button" class="croc-cc-asset glass-panel glass-panel--micro" data-cc-go="devices" data-cc-id="${escapeHtml(id)}" title="${escapeHtml(name)}">
        <span class="croc-cc-asset__ico" aria-hidden="true">${on ? "◉" : "◎"}</span>
        <span class="croc-cc-asset__main">
          <span class="croc-cc-asset__sn">${escapeHtml(name)}</span>
          ${loc ? `<span class="croc-cc-asset__loc">${escapeHtml(loc)}</span>` : ""}
        </span>
        <span class="croc-cc-asset__dot ${on ? "croc-cc-asset__dot--on" : "croc-cc-asset__dot--off"}"></span>
      </button>`;
  }).join("");
  const more =
    unique.length > 12 || all.length > unique.length
      ? ccGoBtn("group/_all", "More boards", "croc-cc-link croc-cc-assets__more")
      : "";
  return rows + more;
}

function deviceLabel(dOrId, detail, summary) {
  if (dOrId && typeof dOrId === "object") {
    const soft = String(dOrId.display_label || "").trim();
    if (soft) return soft;
    return softDeviceLabel(dOrId.device_id);
  }
  const label = detail && (detail.display_label || detail.device_label);
  if (label) return String(label).trim();
  const zone = humanizeZoneLabel(detail && (detail.zone || detail.notification_group));
  if (zone) return zone;
  const sum = String(summary || "");
  const siteMatch = sum.match(/\]\s*([^·]+?)(?:\s*·|\s*$)/);
  if (siteMatch && String(siteMatch[1] || "").trim()) {
    const site = humanizeZoneLabel(siteMatch[1]) || String(siteMatch[1]).trim();
    if (site) return site;
  }
  return softDeviceLabel(dOrId);
}

/** Soft priority band — never dump raw risk integers as the primary label. */
function softPriorityBand(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "Priority · review";
  if (n >= 85) return "Priority · critical";
  if (n >= 70) return "Priority · high";
  if (n >= 40) return "Priority · medium";
  return "Priority · low";
}

export function mapEventToIncidentCard(ev) {
  const level = String(ev.level || "warn").toLowerCase();
  const sev = level === "critical" || level === "error" ? "critical"
    : level === "warn" ? "high" : "medium";
  const detail = ev.detail && typeof ev.detail === "object" ? ev.detail : {};
  const rawTitle = ev.summary || ev.event_type || detail.trigger || "Alarm";
  const responder = String(detail.assigned_responder || "").trim();
  const eta = String(detail.eta || detail.eta_minutes || "").trim();
  const title = humanizeAlarmTitle(rawTitle);
  let zone = deviceLabel(ev.device_id, detail, rawTitle);
  // Avoid "Site · Site" when title already embeds the site — never fall back to raw SN- ids.
  if (zone && zone !== "—" && title.toLowerCase().includes(String(zone).toLowerCase())) {
    zone = "";
  }
  let alarmId = String(detail.alarm_id || "").trim();
  if (!alarmId && (ev.id != null || ev.event_id != null)) {
    alarmId = `alarm-${ev.id != null ? ev.id : ev.event_id}`;
  } else if (alarmId && /^\d+$/.test(alarmId)) {
    alarmId = `alarm-${alarmId}`;
  }
  return {
    sev,
    title,
    zone,
    score: Number(detail.risk_score) > 0 ? Number(detail.risk_score) : null,
    conf: null,
    responder: responder || "",
    eta: eta || "",
    ago: fmtRel(ev.ts_malaysia || ev.ts) || "—",
    thumb: sev,
    eventId: ev.id != null ? ev.id : ev.event_id,
    deviceId: String(ev.device_id || detail.device_id || "").trim(),
    alarmId,
  };
}

function buildIncidentCards(incidents) {
  const list = Array.isArray(incidents) ? incidents : [];
  if (!list.length) {
    return `<p class="muted croc-cc-empty croc-cc-feed__empty">Quiet for now — new alarms land here as they happen. Notify still fires first.</p>`;
  }
  return list.map((inc) => {
    const eid = inc.eventId != null ? String(inc.eventId) : "";
    return `
    <button type="button" class="croc-cc-feed-card croc-cc-feed-card--${inc.sev} glass-panel glass-panel--inset" data-cc-go="incidents"${eid ? ` data-cc-id="${escapeHtml(eid)}"` : ""}>
      <div class="croc-cc-feed-card__shine" aria-hidden="true"></div>
      <div class="croc-cc-feed-card__thumb croc-cc-feed-card__thumb--${inc.thumb}"></div>
      <div class="croc-cc-feed-card__body">
        <div class="croc-cc-feed-card__head">
          <span class="croc-cc-sev croc-cc-sev--${inc.sev}">${escapeHtml(inc.sev === "critical" ? "Critical" : inc.sev === "high" ? "High" : "Medium")}</span>
          <span class="croc-cc-feed-card__time">${escapeHtml(inc.ago)}</span>
        </div>
        <h4 class="croc-cc-feed-card__title">${escapeHtml(inc.title)}</h4>
        ${inc.zone ? `<p class="croc-cc-feed-card__zone">${escapeHtml(inc.zone)}</p>` : ""}
        <div class="croc-cc-feed-card__scores">
          ${inc.score != null ? `<span>${escapeHtml(softPriorityBand(inc.score))}</span>` : ""}
          ${inc.conf != null ? `<span>${inc.conf}% certainty</span>` : ""}
        </div>
        ${inc.responder || inc.eta ? `<div class="croc-cc-feed-card__foot">
          ${inc.responder ? `<span>${escapeHtml(inc.responder)}</span>` : ""}
          ${inc.eta ? `<span>Travel ${escapeHtml(inc.eta)}</span>` : ""}
        </div>` : ""}
      </div>
    </button>`;
  }).join("");
}

function buildResponderRows(responders) {
  const list = Array.isArray(responders) ? responders : [];
  if (!list.length) {
    return `<p class="muted croc-cc-empty">Nothing in your field queue yet. When you are assigned, Accept → On the way → On site appear here.</p>`;
  }
  return list.map((r) => {
    const distSoft = softDeviceLabel(r.dist);
    const distPart = distSoft && distSoft !== "—" ? ` · ${escapeHtml(distSoft)}` : "";
    const did = String(r.dispatchId || r.dispatch_id || "").trim();
    const next = r.nextStatus && r.nextLabel
      ? `<button type="button" class="btn sm btn-tap" data-home-dispatch="${escapeHtml(did)}" data-home-next="${escapeHtml(r.nextStatus)}">${escapeHtml(r.nextLabel)}</button>`
      : "";
    return `
    <div class="croc-cc-responder glass-panel glass-panel--micro ${r.active ? "croc-cc-responder--active" : ""}" data-dispatch-id="${escapeHtml(did)}">
      <span class="croc-cc-responder__av">${escapeHtml(initials(r.name))}</span>
      <span class="croc-cc-responder__info">
        <span class="croc-cc-responder__name">${escapeHtml(r.name)}</span>
        <span class="croc-cc-responder__stat">${escapeHtml(r.status)}${distPart}</span>
      </span>
      ${next ? `<span class="croc-cc-responder__act">${next}</span>` : ""}
    </div>`;
  }).join("");
}

function buildTimeline(items) {
  const rows = Array.isArray(items) ? items : [];
  if (!rows.length) {
    return `<p class="muted croc-cc-empty">Timeline fills when incidents have activity.</p>`;
  }
  return rows.slice(0, 12).map((r) => `
    <div class="croc-cc-timeline__row">
      <span class="croc-cc-timeline__time">${escapeHtml(String(r.at || r.t || "—"))}</span>
      <span class="croc-cc-timeline__dot croc-cc-timeline__dot--ok"></span>
      <span class="croc-cc-timeline__label">${escapeHtml(String(r.summary || r.label || ""))}</span>
    </div>`).join("");
}

function buildSnapshotGridHtml() {
  return `
    <div class="croc-cc-snapshots croc-cc-snapshots--footer" id="ccHomePhotos">
      <p class="muted croc-cc-empty" id="ccHomePhotosEmpty">No scene photos yet. When a camera captures an alarm, thumbs appear here.</p>
      <div class="croc-cc-photo-grid" id="ccHomePhotosGrid" hidden></div>
      <div class="row" style="gap:8px;flex-wrap:wrap;margin-top:8px">
        ${ccGoBtn("snapshots", "Scene photos", "btn sm secondary btn-tap")}
        ${ccGoBtn("account/cameras", "Camera setup", "btn sm ghost btn-tap")}
      </div>
    </div>`;
}

/** Soft HTML for Home photo thumbs (real images when available). */
export function paintHomePhotoThumbsHtml(items) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) {
    return {
      empty: true,
      html: `<p class="muted croc-cc-empty">No scene photos yet. When a camera captures an alarm, thumbs appear here.</p>`,
    };
  }
  const cards = list.slice(0, 6).map((it) => {
    const _sid = String(it.snapshot_id || it.id || "").trim();
    const whenRaw = it.created_at != null ? it.created_at : (it.ts || "");
    let when = "";
    try { when = whenRaw !== "" && whenRaw != null ? fmtRel(whenRaw) : ""; } catch (_) { when = ""; }
    if (when === "—") when = "";
    const label = softDeviceLabel(it.device_id) || softOperatorNote(String(it.camera_label || it.label || "Scene"));
    const src = String(it._imgSrc || "").trim();
    return `
      <button type="button" class="croc-cc-photo-thumb glass-panel glass-panel--micro" data-cc-go="snapshots" title="${escapeHtml(label)}">
        ${src ? `<img src="${escapeHtml(src)}" alt="" loading="lazy" decoding="async" />` : `<span class="croc-cc-photo-thumb__ph" aria-hidden="true">▣</span>`}
        <span class="croc-cc-photo-thumb__cap">
          <span class="croc-cc-photo-thumb__name">${escapeHtml(label)}</span>
          ${when ? `<span class="muted">${escapeHtml(when)}</span>` : ""}
        </span>
      </button>`;
  }).join("");
  return { empty: false, html: cards };
}

/**
 * Soft HTML for Home “People on map” strip (GET /locations/latest).
 * @param {object[]} items
 */
export function paintHomePeopleStripHtml(items) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) {
    return {
      empty: true,
      html: "",
      text: "No shared phone locations yet. When responders share GPS, they appear here and on the map — stale points stay labeled.",
    };
  }
  const cards = list.slice(0, 8).map((it) => {
    const whoRaw = String((it && (it.username || it.user_id || it.display_name)) || "").trim();
    const who = softOperatorNote(whoRaw) || softDeviceLabel(whoRaw) || "Person";
    const stale = !!(it && (it.is_stale === true || it.is_stale === 1 || it.is_stale === "1"));
    const whenRaw = it && (it.recorded_at != null ? it.recorded_at : it.ts);
    let when = "";
    try { when = whenRaw != null && whenRaw !== "" ? fmtRel(whenRaw) : ""; } catch (_) { when = ""; }
    if (when === "—" || when === "\u2014") when = "";
    const reason = stale ? String((it && it.stale_reason) || "outdated").replace(/_/g, " ") : "";
    let acc = "";
    try {
      const n = Number(it && it.accuracy_m);
      if (Number.isFinite(n) && n >= 0) {
        acc = n < 10 ? `±${Math.round(n * 10) / 10} m` : `±${Math.round(n)} m`;
      }
    } catch (_) {
      acc = "";
    }
    const meta = stale
      ? `Stale${reason ? ` · ${reason}` : ""}${acc ? ` · ${acc}` : ""}`
      : `${when || "Fresh"}${acc ? ` · ${acc}` : ""}`;
    const uid = String((it && (it.user_id || it.username)) || whoRaw || "").trim();
    const lat = Number(it && (it.latitude != null ? it.latitude : it.lat));
    const lng = Number(it && (it.longitude != null ? it.longitude : it.lng));
    const latAttr = Number.isFinite(lat) ? String(lat) : "";
    const lngAttr = Number.isFinite(lng) ? String(lng) : "";
    return `
      <button type="button" class="croc-cc-people-chip glass-panel glass-panel--micro${stale ? " croc-cc-people-chip--stale" : ""}"
        data-home-person-pick="1"
        data-user-id="${escapeHtml(uid)}"
        data-person-label="${escapeHtml(who)}"
        data-stale="${stale ? "1" : "0"}"
        data-lat="${escapeHtml(latAttr)}"
        data-lng="${escapeHtml(lngAttr)}"
        title="Select ${escapeHtml(who)} for Assign">
        <span class="croc-cc-people-chip__name">${escapeHtml(who)}</span>
        <span class="muted croc-cc-people-chip__meta">${escapeHtml(meta)}</span>
      </button>`;
  }).join("");
  const freshN = list.filter((it) => !(it && (it.is_stale === true || it.is_stale === 1 || it.is_stale === "1"))).length;
  const staleN = list.length - freshN;
  const summary = staleN > 0
    ? `${freshN} fresh · ${staleN} stale — nearest match prefers fresh`
    : `${freshN} on the map`;
  return {
    empty: false,
    html: `<p class="muted" style="margin:0 0 8px;font-size:12px;line-height:1.4">${escapeHtml(summary)}</p>
      <div class="croc-cc-people-row">${cards}</div>`,
  };
}

/**
 * Open Home Assign workbench and optionally fill device / responder fields.
 * Used by map pins + People strip (must stay in source — tip594 bundle had this ahead of src).
 * @param {ParentNode|null|undefined} view
 * @param {{ deviceId?: string, responder?: string, alarmId?: string, reason?: string, outText?: string }} [pack]
 * @returns {boolean}
 */
export function openHomeAssignWorkbench(view, pack = {}) {
  if (!view || typeof view.querySelector !== "function") return false;
  const wb = view.querySelector("[data-home-wb]");
  const assign = view.querySelector("#ccHomeAssign");
  const mapLanding = (() => {
    try {
      return String(document.body.dataset.ccLanding || "") === "map"
        || String((view.querySelector("#crocCcRoot") || {}).getAttribute?.("data-cc-landing") || "") === "map";
    } catch (_) {
      return false;
    }
  })();
  if (wb) {
    if (mapLanding) {
      // tip597: map is the main screen — fill Assign without covering the map.
      wb.setAttribute("data-wb-dock", "1");
      wb.setAttribute("data-wb-open", "0");
      wb.setAttribute("data-wb-assign-armed", "1");
      try {
        localStorage.setItem("croc_home_wb_dock", "1");
        localStorage.setItem("croc_home_wb_open", "0");
      } catch (_) {}
      const toggleBtn = wb.querySelector("[data-wb-toggle]");
      if (toggleBtn) {
        toggleBtn.setAttribute("aria-expanded", "false");
        toggleBtn.textContent = "Workflow";
      }
      const dockBtn = wb.querySelector("[data-wb-dock-toggle]");
      if (dockBtn) {
        dockBtn.setAttribute("aria-expanded", "false");
        dockBtn.textContent = "Open";
      }
      const chip = wb.querySelector("[data-wb-dock-tab]");
      if (chip) {
        chip.setAttribute("aria-expanded", "false");
        chip.setAttribute("data-wb-assign-pulse", "1");
      }
    } else {
      wb.setAttribute("data-wb-dock", "0");
      wb.setAttribute("data-wb-open", "1");
      try {
        localStorage.setItem("croc_home_wb_dock", "0");
        localStorage.setItem("croc_home_wb_open", "1");
      } catch (_) {}
      const toggleBtn = wb.querySelector("[data-wb-toggle]");
      if (toggleBtn) {
        toggleBtn.setAttribute("aria-expanded", "true");
        toggleBtn.textContent = "Hide";
      }
      const dockBtn = wb.querySelector("[data-wb-dock-toggle]");
      if (dockBtn) {
        dockBtn.setAttribute("aria-expanded", "true");
        dockBtn.textContent = "Fold";
      }
      const chip = wb.querySelector("[data-wb-dock-tab]");
      if (chip) chip.setAttribute("aria-expanded", "true");
    }
  }
  const deviceId = String(pack.deviceId || "").trim();
  const responder = String(pack.responder || "").trim();
  const alarmId = String(pack.alarmId || "").trim();
  const reason = String(pack.reason || "").trim();
  const outText = String(pack.outText || "").trim();
  const deviceEl = view.querySelector("#ccHomeAssign #dcDeviceId") || view.querySelector("#dcDeviceId");
  const responderEl = view.querySelector("#ccHomeAssign #dcResponder") || view.querySelector("#dcResponder");
  const alarmEl = view.querySelector("#ccHomeAssign #dcAlarmId") || view.querySelector("#dcAlarmId");
  const reasonEl = view.querySelector("#ccHomeAssign #dcReason") || view.querySelector("#dcReason");
  const out = view.querySelector("#ccHomeAssign #dcOut") || view.querySelector("#dcOut");
  if (deviceEl && deviceId) deviceEl.value = deviceId;
  if (responderEl && responder) responderEl.value = responder;
  if (alarmEl && alarmId) alarmEl.value = alarmId;
  if (reasonEl && reason && !String(reasonEl.value || "").trim()) reasonEl.value = reason.slice(0, 200);
  if (out && outText) out.textContent = outText;
  try {
    if (responder && responderEl) responderEl.focus();
    else if (deviceId && deviceEl) deviceEl.focus();
  } catch (_) {}
  try {
    if (assign && typeof assign.scrollIntoView === "function") {
      assign.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  } catch (_) {}
  try {
    window.dispatchEvent(new Event("resize"));
  } catch (_) {}
  return !!(wb || assign);
}

/**
 * Soft HTML for Home Proof & close card (real proof-of-response + SLA when available).
 * @param {{ eventId?: string, proof?: object|null, sla?: object|null, emptyReason?: string }} pack
 */
export function paintHomeProofStatus(pack = {}) {
  const eventId = String(pack.eventId || "").trim();
  const proof = pack.proof && typeof pack.proof === "object" ? pack.proof : null;
  const sla = pack.sla && typeof pack.sla === "object" ? pack.sla : null;
  const emptyReason = String(pack.emptyReason || "").trim();

  if (!eventId) {
    return {
      status: emptyReason || "No open case yet — proof appears after an alarm lands.",
      meta: "",
      eventId: "",
    };
  }

  if (emptyReason && !proof && !sla) {
    return {
      status: emptyReason,
      meta: "",
      eventId,
    };
  }

  const closure = (proof && proof.closure) || {};
  const commander = (proof && proof.commander) || {};
  const verified = !!closure.verified;
  const reason = typeof humanizeProofReason === "function"
    ? humanizeProofReason(closure.reason || (verified ? "verified" : "pending"))
    : String(closure.reason || (verified ? "verified" : "pending"));
  const lead = String(commander.username || commander.assigned_responder || "").trim();
  const overall = sla && sla.overall_status
    ? (typeof humanizeProofReason === "function"
      ? humanizeProofReason(sla.overall_status)
      : String(sla.overall_status).replace(/_/g, " "))
    : "";

  const status = verified
    ? `Verified · ${reason}`
    : `Not verified · ${reason}`;
  const metaBits = [];
  if (overall && overall !== "—") metaBits.push(`Timing: ${overall}`);
  if (lead) metaBits.push(`Lead: ${lead}`);
  return {
    status,
    meta: metaBits.join(" · "),
    eventId,
  };
}

/**
 * Apply paintHomeProofStatus into the Home Proof card DOM.
 * @param {ParentNode} view
 * @param {{ eventId?: string, proof?: object|null, sla?: object|null, emptyReason?: string }} pack
 */
export function applyHomeProofPanel(view, pack = {}) {
  if (!view) return;
  const painted = paintHomeProofStatus(pack);
  const statusEl = view.querySelector("#ccHomeProofStatus");
  const metaEl = view.querySelector("#ccHomeProofMeta");
  const linkEl = view.querySelector("#ccHomeProofLink");
  if (statusEl) statusEl.textContent = painted.status;
  if (metaEl) {
    if (painted.meta) {
      metaEl.hidden = false;
      metaEl.textContent = painted.meta;
    } else {
      metaEl.hidden = true;
      metaEl.textContent = "";
    }
  }
  if (linkEl) {
    linkEl.setAttribute("data-cc-go", "incidents");
    if (painted.eventId) linkEl.setAttribute("data-cc-id", String(painted.eventId));
    else linkEl.removeAttribute("data-cc-id");
  }
}

/**
 * Tip 501/503: Soft-map first pending AI plan into MAIC §5.5 cards (category · score · why).
 * @param {object|null|undefined} aiPack
 * @returns {{ waitingN: number, category?: string, riskScore?: number|null, reasonLine?: string, responderHint?: string }}
 */
function maicFieldsFromAiPack(aiPack) {
  return maicFieldsFromAiItems(aiPack && aiPack.items);
}

function buildHomeAssignHtml() {
  return `
    <div class="croc-cc-home-assign" id="ccHomeAssign" data-home-assign="1">
      <p class="muted" style="margin:0 0 10px;font-size:13px;line-height:1.45">Assign a person to a live alert. <strong>Find nearest · Fresh-only Soft</strong> uses shared App GPS (straight-line, same visibility as the map) — stale pins excluded · not fabric reach yet · no manual map north/east needed.</p>
      <div class="inline-form croc-cc-home-assign__form">
        <label class="field grow"><span>Device</span>
          <input id="dcDeviceId" maxlength="128" placeholder="Device name…" autocomplete="off" />
        </label>
        <label class="field grow"><span>Person</span>
          <input id="dcResponder" maxlength="64" placeholder="Account name (optional)" autocomplete="off" />
        </label>
        <label class="field grow"><span>Note</span>
          <input id="dcReason" maxlength="200" placeholder="Short reason" />
        </label>
        <input type="hidden" id="dcAlarmId" value="" />
        <input type="hidden" id="dcRisk" value="70" />
        <input type="hidden" id="dcLat" value="" />
        <input type="hidden" id="dcLng" value="" />
        <div class="row" style="gap:8px;flex-wrap:wrap">
          <button type="button" class="btn sm btn-tap" id="dcCreate">Assign now</button>
          <button type="button" class="btn sm secondary btn-tap" id="dcPreview">Find nearest · Fresh-only Soft</button>
          <button type="button" class="btn sm ghost btn-tap" id="dcAuto">Auto-assign Soft</button>
        </div>
      </div>
      <p id="dcOut" class="muted" style="margin-top:8px;min-height:1.2em;font-size:13px" aria-live="polite"></p>
    </div>`;
}

function buildHomeAlertPickHtml(incidentCards, opts = {}) {
  const list = Array.isArray(incidentCards) ? incidentCards : [];
  const canAssign = opts.canAssign !== false;
  if (!list.length) {
    return `
    <div class="croc-cc-home-alerts croc-cc-home-alerts--ready glass-panel glass-panel--inset" id="ccHomeAlerts">
      <p class="croc-cc-home-alerts__ready-title">Ready for the next alarm</p>
      <p class="muted croc-cc-empty" style="margin:0 0 12px">No live alarms yet. Keep the loop warm — notify still fires first when something lands.</p>
      <div class="croc-cc-home-alerts__ready-acts">
        ${canAssign
          ? `${ccGoBtn("readiness", "Before tonight", "btn sm btn-tap")}
             ${ccGoBtn("auto-sentinel", "Policy", "btn sm secondary btn-tap")}
             ${ccGoBtn("response-plans", "Plans", "btn sm secondary btn-tap")}
             ${ccGoBtn("response", "Assignments", "btn sm ghost btn-tap")}`
          : `${ccGoBtn("response", "My assignments", "btn sm btn-tap")}
             ${ccGoBtn("incidents", "Cases", "btn sm secondary btn-tap")}
             ${ccGoBtn("readiness", "Before tonight", "btn sm ghost btn-tap")}`}
      </div>
    </div>`;
  }
  return `
    <div class="croc-cc-home-alerts" id="ccHomeAlerts">
      ${list.slice(0, 4).map((inc) => {
        const eid = inc.eventId != null ? String(inc.eventId) : "";
        const did = String(inc.deviceId || "").trim();
        const aid = String(inc.alarmId || (eid ? `alarm-${eid}` : "")).trim();
        return `
        <div class="croc-cc-home-alert glass-panel glass-panel--micro">
          <div class="croc-cc-home-alert__meta">
            <span class="croc-cc-sev croc-cc-sev--${escapeHtml(inc.sev || "medium")}">${escapeHtml(inc.sev === "critical" ? "Critical" : inc.sev === "high" ? "High" : "Medium")}</span>
            <span class="muted">${escapeHtml(inc.ago || "")}</span>
          </div>
          <p class="croc-cc-home-alert__title">${escapeHtml(inc.title || "Alarm")}</p>
          <div class="row" style="gap:8px;flex-wrap:wrap;margin-top:8px">
            ${canAssign ? `<button type="button" class="btn sm btn-tap"
              data-home-alert-pick="1"
              data-device-id="${escapeHtml(did)}"
              data-alarm-id="${escapeHtml(aid)}"
              data-event-id="${escapeHtml(eid)}"
              data-title="${escapeHtml(inc.title || "Alarm")}">Use for assign</button>` : ""}
            ${ccGoBtn("incidents", canAssign ? "Open case" : "Open case · proof", `btn sm ${canAssign ? "ghost" : ""} btn-tap`, eid)}
          </div>
        </div>`;
      }).join("")}
    </div>`;
}

/**
 * Home closed-loop workbench — real actions stay on Home; Settings only for policy.
 * @param {object[]} incidentCards
 * @param {object[]} responders
 * @param {object[]} timelineItems
 * @param {object|null} aiPack
 * @param {{ isAdmin?: boolean }} [opts]
 */
function buildHomeWorkbenchHtml(incidentCards, responders, timelineItems, aiPack, opts = {}) {
  const isAdmin = !!opts.isAdmin;
  const waitingN = Array.isArray(aiPack && aiPack.items)
    ? aiPack.items.filter((it) => {
      const st = String((it && it.status) || "").toLowerCase();
      return !st || st === "pending" || st === "open";
    }).length
    : 0;
  const pulseState = waitingN > 0 ? "interrupt" : "watching";
  const pulseLine = waitingN > 0
    ? (waitingN === 1 ? "1 Needs You — Hold / Takeover or open Mission Room" : `${waitingN} Needs You — Hold / Takeover or open Mission Room`)
    : (isAdmin
      ? "Sentinel is watching — finish assign and field steps here when an alarm lands"
      : "When you are assigned, Accept → On the way → On site stay on this Home workbench");
  const proofId = (() => {
    const first = Array.isArray(incidentCards) && incidentCards[0];
    return first && first.eventId != null ? String(first.eventId) : "";
  })();
  const headCta = isAdmin
    ? ccGoBtn("auto-sentinel", "Policy", "btn sm secondary btn-tap")
    : ccGoBtn("response", "My assignments", "btn sm secondary btn-tap");
  const fieldCard = `
      <article class="croc-cc-workbench__card glass-panel glass-panel--inset croc-cc-workbench__field${isAdmin ? "" : " croc-cc-workbench__field--lead"}">
        <h4 class="croc-cc-workbench__card-title">My field queue</h4>
        <p class="muted" style="margin:0 0 8px;font-size:13px;line-height:1.45">${isAdmin
          ? "Your own Accept → On the way → On site steps when you are the assignee."
          : "Your living response steps — Accept, travel, arrive, then finish with proof on the case."}</p>
        <div class="croc-cc-responders" id="ccHomeQueue">${buildResponderRows(responders)}</div>
      </article>`;
  const proofCard = `
      <article class="croc-cc-workbench__card glass-panel glass-panel--inset croc-cc-workbench__proof" id="ccHomeProof">
        <h4 class="croc-cc-workbench__card-title">Proof &amp; close</h4>
        <p class="muted" style="margin:0 0 8px;font-size:13px;line-height:1.45" id="ccHomeProofStatus">No open case yet — proof appears after an alarm lands.</p>
        <p class="muted" style="margin:0 0 10px;font-size:12px;line-height:1.4" id="ccHomeProofMeta" hidden></p>
        <div class="row" style="gap:8px;flex-wrap:wrap">
          ${ccGoBtn("incidents", "Open case proof", "btn sm btn-tap", proofId).replace("<button ", '<button id="ccHomeProofLink" ')}
          ${ccGoBtn("incidents", "All cases", "btn sm ghost btn-tap")}
        </div>
      </article>`;
  const adminCards = isAdmin ? `
      <article class="croc-cc-workbench__card glass-panel glass-panel--inset croc-cc-workbench__alerts">
        <h4 class="croc-cc-workbench__card-title">Named alarms</h4>
        <p class="muted" style="margin:0 0 8px;font-size:13px;line-height:1.45">Name the alarm, then send a person. Continue is not Approve.</p>
        ${buildHomeAlertPickHtml(incidentCards)}
      </article>
      <article class="croc-cc-workbench__card glass-panel glass-panel--inset croc-cc-workbench__ai">
        ${buildAutoSentinelHomeHtml(incidentCards, aiPack)}
      </article>
      <article class="croc-cc-workbench__card glass-panel glass-panel--inset">
        <h4 class="croc-cc-workbench__card-title">Assign people</h4>
        ${buildHomeAssignHtml()}
      </article>` : `
      <article class="croc-cc-workbench__card glass-panel glass-panel--inset croc-cc-workbench__alerts">
        <h4 class="croc-cc-workbench__card-title">Named alarms</h4>
        <p class="muted" style="margin:0 0 8px;font-size:13px;line-height:1.45">Open a case for timeline and proof. Assignments come from your admin.</p>
        ${buildHomeAlertPickHtml(incidentCards, { canAssign: false })}
      </article>
      <article class="croc-cc-workbench__card glass-panel glass-panel--inset croc-cc-workbench__ai">
        ${buildAutoSentinelHomeHtml(incidentCards, { aiOn: false })}
      </article>`;
  return `
  ${buildTonightWatchHtml(aiPack, { isAdmin })}
  <details class="croc-tonight-tech glass-panel glass-panel--inset" data-tonight-tech="1">
    <summary class="croc-tonight-tech__sum">More about tonight</summary>
    ${buildAiContextDeepHtml(aiPack, { isAdmin })}
  </details>
  <section class="croc-cc-workbench glass-panel glass-panel--panel glass-panel--float croc-cc-workbench--frost" data-home-wb="1" data-home-role="${isAdmin ? "admin" : "user"}" data-wb-open="0" data-wb-dock="1" aria-label="Tonight workbench">
    <button type="button" class="croc-cc-wb-chip btn-tap" data-wb-dock-tab="1" aria-expanded="false" title="Open tonight workbench">Tonight</button>
    <header class="croc-cc-workbench__head">
      <div class="croc-cc-workbench__head-copy">
        <p class="incident-workspace__eyebrow">Tonight</p>
        <h3 class="croc-cc-workbench__title">${isAdmin ? "Continue · Needs You · proof" : "Named · travel · proof"}</h3>
        <p class="muted croc-cc-workbench__lede">${isAdmin
          ? "The Watch continues. You interrupt only when a fact or a person is missing. Close needs two-lens proof."
          : "Stay on Home for Accept · travel · arrive. Open a case only when you need proof."}</p>
        <p class="muted croc-cc-workbench__compact-line" id="ccHomePulseLineCompact">${escapeHtml(pulseLine)}</p>
      </div>
      <div class="croc-cc-workbench__head-acts">
        <button type="button" class="btn sm secondary btn-tap croc-cc-workbench__expand" data-wb-toggle="1" aria-expanded="false">Workflow</button>
        ${headCta}
        ${ccGoBtn("readiness", "Before", "btn sm ghost btn-tap croc-cc-workbench__before")}
        <button type="button" class="btn sm ghost btn-tap croc-cc-workbench__dock" data-wb-dock-toggle="1" aria-expanded="true" title="Fold workbench away">Fold</button>
      </div>
    </header>
    <div class="croc-as-pulse croc-cc-workbench__pulse" data-as-pulse="${escapeHtml(pulseState)}" aria-live="polite">
      <div class="croc-as-pulse__stages" role="list">
        <span class="croc-as-pulse__stage${pulseState === "watching" ? " is-lit" : ""}" role="listitem"><i aria-hidden="true"></i>Watching</span>
        <span class="croc-as-pulse__stage${pulseState === "acting" ? " is-lit" : ""}" role="listitem"><i aria-hidden="true"></i>Acting</span>
        <span class="croc-as-pulse__stage${pulseState === "interrupt" ? " is-lit" : ""}" role="listitem"><i aria-hidden="true"></i>Needs you</span>
      </div>
      <p class="croc-as-pulse__line" id="ccHomePulseLine">${escapeHtml(pulseLine)}</p>
    </div>
    <div class="croc-cc-workbench__body" data-wb-body="1">
    <div class="croc-cc-workbench__grid">
      ${isAdmin ? "" : fieldCard}
      ${adminCards}
      ${isAdmin ? fieldCard : ""}
      <article class="croc-cc-workbench__card glass-panel glass-panel--inset">
        <h4 class="croc-cc-workbench__card-title">Scene photos</h4>
        ${buildSnapshotGridHtml()}
      </article>
      <article class="croc-cc-workbench__card glass-panel glass-panel--inset croc-cc-workbench__people" id="ccHomePeople">
        <h4 class="croc-cc-workbench__card-title">People on map</h4>
        <p class="muted croc-cc-empty" id="ccHomePeopleEmpty" style="margin:0">No shared phone locations yet. When responders share GPS, they appear here and on the map — stale points stay labeled.</p>
        <div id="ccHomePeopleBody" hidden></div>
        <button type="button" class="btn sm ghost btn-tap" data-cc-go="overview" data-home-map-tab="1" style="margin-top:10px">Open map</button>
      </article>
      ${proofCard}
      <article class="croc-cc-workbench__card glass-panel glass-panel--inset croc-cc-workbench__timeline">
        <h4 class="croc-cc-workbench__card-title">Recent activity</h4>
        <div class="croc-cc-timeline">${buildTimeline(timelineItems)}</div>
      </article>
    </div>
    ${isAdmin
      ? `<p class="muted croc-cc-ai-honesty" style="margin:var(--ds-space-2,12px) 0 0;font-size:var(--ds-text-caption,0.8125rem);line-height:1.45;max-width:62ch">Notify first. Rules triage. Show on map to see the pin. Continue stays on the loop. Policy is history, not the live gate. A short why may appear. Pictures do not change the score.</p>`
      : `
    <div class="croc-as-innov croc-as-innov--home croc-as-innov--user" role="list" aria-label="Your response steps">
      <article class="croc-as-innov__card glass-panel glass-panel--inset" role="listitem">
        <p class="croc-as-innov__k">Notify-first</p>
        <strong class="croc-as-innov__v">You get the call</strong>
        <p class="muted croc-as-innov__s">Alerts reach you before planning</p>
      </article>
      <article class="croc-as-innov__card glass-panel glass-panel--inset" role="listitem">
        <p class="croc-as-innov__k">Field</p>
        <strong class="croc-as-innov__v">Accept · travel · arrive</strong>
        <p class="muted croc-as-innov__s">Your queue stays on Home</p>
      </article>
      <article class="croc-as-innov__card glass-panel glass-panel--inset" role="listitem">
        <p class="croc-as-innov__k">Proof</p>
        <strong class="croc-as-innov__v">Close with evidence</strong>
        <p class="muted croc-as-innov__s">Open the case when done</p>
      </article>
    </div>`}
    </div>
  </section>`;
}

/**
 * Home Auto Sentinel panel — runs by default; humans only interrupt.
 * @param {object[]} incidents
 * @param {{ items?: object[], count?: number, settings?: object, aiOn?: boolean } | null} aiPack
 */
function softAiHomeStatus(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (s === "pending" || s === "open") return "Needs you";
  if (s === "approved" || s === "continuing") return "Continuing";
  if (s === "rejected") return "Held";
  if (s === "done" || s === "closed") return "Done";
  return softOperatorNote(s) || "Auto plan";
}

function softAiHomeLabel(it) {
  const summary = softOperatorNote(String(
    (it && (it.summary || it.title || it.recommendation || it.detail)) || "",
  )).trim();
  if (summary && summary.length >= 4 && !/^(unknown|n\/?a|—|-)$/i.test(summary)) {
    // Full text — CSS line-clamp on rail; Spatial AI sheet shows untruncated
    return summary;
  }
  return softAiHomeStatus(it && it.status);
}

function softAiEscalationBadgeHtml(it) {
  const evidence = (it && it.evidence && typeof it.evidence === "object" && it.evidence)
    || (it && it.evidence_json && typeof it.evidence_json === "object" && it.evidence_json)
    || {};
  const escalation = evidence.escalation && typeof evidence.escalation === "object"
    ? evidence.escalation
    : null;
  if (!escalation || Number(escalation.tier) !== 2) return "";
  return `<span class="badge warn" title="No Allow before the approval deadline">Tier-2 · approval timeout</span>`;
}

export function buildAutoSentinelHomeHtml(incidents, aiPack = null) {
  const list = Array.isArray(incidents) ? incidents : [];
  const pack = aiPack && typeof aiPack === "object" ? aiPack : {};
  const aiOn = pack.aiOn !== false;
  const items = Array.isArray(pack.items) ? pack.items : [];
  const waiting = items.filter((it) => {
    const st = String((it && it.status) || "").toLowerCase();
    return !st || st === "pending" || st === "open";
  });
  const modeLine = !aiOn
    ? "Auto Sentinel is not open for this account yet."
    : waiting.length > 0
      ? "Needs You — the Team is waiting on this Watch. Continue stays on the loop."
      : "The Team runs this Watch. Continue is on the loop. Needs You is the interrupt.";

  if (!aiOn) {
    return `
    <div class="croc-cc-auto-sentinel" data-auto-home="1">
      <div class="croc-cc-auto-sentinel__head">
        <h4>Auto Sentinel</h4>
        <span class="badge neutral">Ready</span>
      </div>
      <p class="muted croc-cc-empty">Auto plans open when AI is on for staff. Use Before tonight and Plans now — Hold · Takeover stays on Home map when an alarm lands.</p>
      <div class="row" style="gap:8px;flex-wrap:wrap;margin-top:8px">
        ${ccGoBtn("overview", "Home map", "btn sm btn-tap")}
        ${ccGoBtn("readiness", "Before tonight", "btn sm secondary btn-tap")}
        ${ccGoBtn("response-plans", "Plans", "btn sm ghost btn-tap")}
      </div>
      <p class="muted" style="margin:8px 0 0;font-size:11px;line-height:1.4;max-width:52ch">Identity hint only · Soft role is not your Sentinel login · deeper Twin still off.</p>
    </div>`;
  }

  const interruptRows = waiting.slice(0, 3).map((it) => {
    const id = String((it && (it.incident_id || it.id || it.item_id)) || "");
    const risk = it && it.risk_score != null ? it.risk_score : (it && it.risk != null ? it.risk : null);
    const band = risk != null ? softPriorityBand(risk) : "Priority · review";
    const cat = softOperatorNote(String(
      (it && (it.category || it.classification || it.alarm_class)) || "Security review",
    ));
    const escalationBadge = softAiEscalationBadgeHtml(it);
    return `
    <div class="croc-cc-auto-sentinel__row glass-panel glass-panel--micro" data-ai-id="${escapeHtml(id)}">
      <div class="croc-cc-auto-sentinel__main">
        <button type="button" class="croc-cc-auto-sentinel__label btn ghost btn-tap" data-ai-map-focus="${escapeHtml(id)}">${escapeHtml(softAiHomeLabel(it))}</button>
        <p class="muted croc-cc-auto-sentinel__meta">${escapeHtml(cat)} · ${escapeHtml(band)} ${escalationBadge}</p>
      </div>
      <div class="croc-cc-auto-sentinel__acts">
        ${decisionSurfaceActionsHtml({
          incidentId: id,
          context: "home",
          compact: true,
          waiting: true,
          actions: Array.isArray(aiPack && aiPack.decision_actions) ? aiPack.decision_actions : undefined,
        })}
        <button type="button" class="btn sm ghost btn-tap" data-ai-map-focus="${escapeHtml(id)}" data-ai-map-tab="assessment">On map</button>
        <button type="button" class="btn sm ghost btn-tap" data-cc-go="auto-sentinel">Policy</button>
      </div>
    </div>`;
  }).join("");

  const alarmRows = list.slice(0, 3).map((inc) => {
    const eid = inc.eventId != null ? String(inc.eventId) : "";
    return `
    <div class="croc-cc-risk-row">
      <button type="button" class="croc-cc-risk-link" data-cc-go="incidents"${eid ? ` data-cc-id="${escapeHtml(eid)}"` : ""}>${escapeHtml(String(inc.title || "Alarm"))}</button>
    </div>`;
  }).join("");

  const waitingN = waiting.length;
  const statusBadge = waitingN > 0
    ? `<span class="badge warn intelligence-auto-pill">${waitingN === 1 ? "1 needs you" : `${waitingN} need you`}</span>`
    : `<span class="badge online intelligence-auto-pill">Watching</span>`;

  return `
    <div class="croc-cc-auto-sentinel" data-auto-home="1">
      <div class="croc-cc-auto-sentinel__head">
        <h4>Auto Sentinel</h4>
        ${statusBadge}
      </div>
      <p class="muted croc-cc-auto-sentinel__lede">${escapeHtml(modeLine)}</p>
      ${waitingN
        ? `<div class="croc-cc-auto-sentinel__list">${interruptRows}</div>
           <p class="muted" style="margin:8px 0 0;font-size:12px">On the loop: Hold · Takeover · Escalate · More evidence — one interrupt. <button type="button" class="croc-tonight__jump" data-cc-go="response/cases">Open Mission Room</button></p>`
        : `<p class="muted croc-cc-empty" id="ccAutoWatchCopy">No interrupts waiting. The Watch plans new alarms automatically.</p>
           <div class="row" style="gap:8px;flex-wrap:wrap;margin-top:8px">
             <button type="button" class="btn sm btn-tap" id="ccAutoScanBtn">Scan recent alarms</button>
             <button type="button" class="btn sm secondary btn-tap" data-cc-go="auto-sentinel">Policy</button>
           </div>`}
      ${alarmRows
        ? `<div class="croc-cc-auto-sentinel__alarms" style="margin-top:12px">
             <p class="croc-cc-auto-sentinel__alarms-k">Recent alarms</p>
             <div class="croc-cc-risk-list">${alarmRows}</div>
           </div>`
        : ""}
      <button type="button" class="croc-cc-link" style="margin-top:10px;display:inline-block" data-cc-go="auto-sentinel">Policy and history</button>
      <p class="muted" style="margin:6px 0 0;font-size:12px"><strong>On map</strong> opens the pin. Scene text is preview. Continue is the same on-the-loop interrupt. Pictures do not change the score.</p>
      <a href="https://cta.crocnexus.com/" class="croc-cc-link" style="margin-top:6px;display:inline-block" target="_blank" rel="noopener noreferrer" title="Croc Twin Assets · separate site">Open Croc Twin Assets</a>
      <p class="muted" style="margin:4px 0 0;font-size:11px;line-height:1.4;max-width:52ch">Identity hint only · Soft role is not your Sentinel login · deeper Twin still off.</p>
    </div>`;
}

/** @deprecated name kept for call sites — delegates to Auto Sentinel home panel */
function _buildAiRiskAnalysisHtml(incidents, aiPack = null) {
  return buildAutoSentinelHomeHtml(incidents, aiPack);
}

/** @param {object} n @param {object} me @param {(min: string) => boolean} hasRoleFn @param {(cap: string) => boolean} canFn */
function ccNavItemVisible(n, me, hasRoleFn, canFn) {
  if (!me) return false;
  if (n.mapNavEligible) return !!canUseOpsMap(me);
  if (Array.isArray(n.navCatalogAny) && n.navCatalogAny.length) {
    if (!catalogAnyEnabled(me, n.navCatalogAny)) return false;
  } else if (n.navCatalogId && !catalogEnabled(me, n.navCatalogId)) {
    return false;
  }
  if (n.navGate && !me[n.navGate]) return false;
  if (n.navRequiresCap && typeof canFn === "function" && !canFn(n.navRequiresCap)) return false;
  if (n.hideChildAdmin && String(me.manager_admin || "").trim()) return false;
  const r = String(me.role || "").trim();
  if (r === "sa_staff") {
    if (n.hideSaStaff) return false;
    const caps = new Set((me.sa_capabilities || []).map(String));
    const capOk = (need) => caps.has("sa_full_access") || caps.has(need);
    if (n.navCap) return capOk(n.navCap);
    if (String(n.min || "") === "superadmin" && n.saStaffNav) {
      return Boolean(me.ops_dashboard_eligible);
    }
    return ROLE_WEIGHT[r] >= ROLE_WEIGHT[n.min || "user"];
  }
  return hasRoleFn(n.min);
}

/**
 * @param {object} me
 * @param {{ hasRole?: (min: string) => boolean, can?: (cap: string) => boolean }} deps
 */
export function filterCcNavGroups(me, deps = {}) {
  const hasRoleFn = deps.hasRole || (() => false);
  const canFn = deps.can || (() => false);
  const groups = [];
  for (const g of NAV_GROUPS) {
    let items = g.items.filter((n) => ccNavItemVisible(n, me, hasRoleFn, canFn));
    if (items.length === 0) continue;

    if (g.title === "Assets") {
      items = items.map((n) => {
        if (n.label === "Groups") {
          return { ...n, label: "Digital · Groups" };
        }
        if (n.id === "devices" || n.label === "All devices" || n.label === "Devices") {
          return { ...n, label: "All boards" };
        }
        if (n.id === "activate" || n.label === "Activate device") {
          return { ...n, label: "Activate board" };
        }
        if (n.label === "Cameras") {
          return { ...n, label: "Physical · Cameras" };
        }
        if (n.label === "CTA · FDE") {
          return { ...n, label: "Spatial · CTA" };
        }
        if (n.label === "Site") {
          return { ...n, label: "Spatial · Site" };
        }
        if (n.label === "Activate") {
          return { ...n, label: "Physical · Activate" };
        }
        return n;
      });
    }
    if (g.title === "Operations") {
      items = items.map((n) => {
        if (n.id === "readiness" || n.label === "Readiness") {
          return { ...n, label: "Before tonight" };
        }
        return n;
      });
    }
    if (g.title === "Administration") {
      items = items.map((n) => {
        if (n.label === "AI policy" || n.label === "Governance · AI policy") {
          return { ...n, label: "Governance · AI policy" };
        }
        return n;
      });
    }

    groups.push({
      key: g.title,
      label: CC_NAV_LABELS[g.title] || g.title,
      items,
    });
  }
  return groups;
}

/** @param {object} n @param {string} hash @param {string} hashNoQuery */
function ccNavItemActive(n, hash, hashNoQuery) {
  const homeHubActive = hashNoQuery === "#/overview";
  if (n.path === "#/group/_all" || n.path === "#/devices") {
    return hashNoQuery === "#/group/_all"
      || hashNoQuery === "#/devices"
      || hashNoQuery.startsWith("#/devices/");
  }
  if (n.path === "#/site") return hashNoQuery === "#/site";
  if (n.id === "overview") return homeHubActive;
  if (n.id === "account") {
    return hashNoQuery === "#/account" || hashNoQuery.startsWith("#/account/");
  }
  if (n.id === "settings") {
    return hashNoQuery === "#/settings"
      || hashNoQuery.startsWith("#/admin")
      || hashNoQuery === "#/billing"
      || hashNoQuery.startsWith("#/telegram");
  }
  return hash.startsWith(n.path);
}

/** @param {{ label: string, items: object[] }[]} navGroups @param {string} [currentHash] */
function buildCommandCenterTopNavHtml(navGroups, currentHash = "#/overview") {
  const hash = currentHash || "#/overview";
  const hashNoQuery = hash.split("?")[0];
  return navGroups.map((g) => {
    const groupActive = g.items.some((n) => ccNavItemActive(n, hash, hashNoQuery));
    const itemsHtml = g.items.map((n) => {
      const active = ccNavItemActive(n, hash, hashNoQuery);
      const raw = String(n.path || "").replace(/^#\/?/, "");
      const q = raw.indexOf("?");
      const dest = q >= 0 ? raw.slice(0, q) : raw;
      let extra = "";
      if (q >= 0) {
        try { extra = new URLSearchParams(raw.slice(q + 1)).get("tab") || ""; } catch (_) {}
      }
      return `<button type="button" class="croc-cc-nav-dd__item${active ? " croc-cc-nav-dd__item--active" : ""}" role="menuitem" data-cc-go="${escapeHtml(dest)}"${extra ? ` data-cc-id="${escapeHtml(extra)}"` : ""}>
        <span class="croc-cc-nav-dd__ico" aria-hidden="true">${escapeHtml(n.ico || "")}</span>
        <span class="croc-cc-nav-dd__label">${escapeHtml(n.label)}</span>
      </button>`;
    }).join("");
    return `<div class="croc-cc-nav-dd${groupActive ? " croc-cc-nav-dd--active" : ""}" data-cc-nav-dd>
      <button type="button" class="croc-cc-nav-tab croc-cc-nav-dd__trigger${groupActive ? " croc-cc-nav-tab--active" : ""}" aria-expanded="false" aria-haspopup="true">
        <span>${escapeHtml(g.label)}</span>
        <span class="croc-cc-nav-dd__chev" aria-hidden="true">▾</span>
      </button>
      <div class="croc-cc-nav-dd__menu glass-panel glass-panel--float" role="menu" hidden>${itemsHtml}</div>
    </div>`;
  }).join("");
}

/** Routes that mount their own full-screen CC / map chrome (no shared page shell). */
const CC_IMMERSIVE_ROUTE_IDS = new Set(["overview"]);

/** @param {string} routeId */
export function isCommandCenterImmersiveRoute(routeId) {
  return CC_IMMERSIVE_ROUTE_IDS.has(String(routeId || ""));
}

/**
 * Shared CC top header (overview full layout + sub-route page shell).
 * @param {{ me?: object, systemHealth?: number, hasRole?: (min: string) => boolean, can?: (cap: string) => boolean, currentHash?: string }} ctx
 */
function buildCommandCenterHeaderHtml(ctx = {}) {
  const me = ctx.me || {};
  const health =
    ctx.systemHealth != null && Number.isFinite(Number(ctx.systemHealth))
      ? Math.round(Number(ctx.systemHealth))
      : null;
  const user = escapeHtml(String(me.username || me.display_name || "Operator"));
  const roleRaw = String(me.role || "admin");
  const roleSoft =
    roleRaw === "superadmin"
      ? "Platform admin"
      : roleRaw === "sa_staff"
        ? "Staff"
        : roleRaw === "admin"
          ? "Admin"
          : roleRaw === "user"
            ? "User"
            : roleRaw.replace(/_/g, " ");
  const role = escapeHtml(roleSoft);
  const navGroups = filterCcNavGroups(me, { hasRole: ctx.hasRole, can: ctx.can });
  const navHtml = buildCommandCenterTopNavHtml(navGroups, ctx.currentHash || "#/overview");
  const onlineN =
    ctx.onlineDevices != null && Number.isFinite(Number(ctx.onlineDevices))
      ? Math.max(0, Math.round(Number(ctx.onlineDevices)))
      : null;
  const totalN =
    ctx.totalDevices != null && Number.isFinite(Number(ctx.totalDevices))
      ? Math.max(0, Math.round(Number(ctx.totalDevices)))
      : null;
  const healthValue =
    onlineN != null && totalN != null
      ? `${onlineN} of ${totalN}`
      : onlineN != null
        ? String(onlineN)
        : health != null
          ? `${health}%`
          : "—";
  const healthHtml = `<span class="croc-cc-top__health glass-panel glass-panel--micro" title="Devices currently online">
        <span class="croc-cc-top__health-k">Devices online</span>
        <span class="croc-cc-top__health-v mono" id="ccSystemHealth">${escapeHtml(healthValue)}</span>
      </span>`;
  return `
  <header class="croc-cc-top glass-panel glass-panel--hero">
    <div class="croc-cc-top__brand">
      <img
        class="croc-cc-top__logo"
        src="${BRAND_LOGO_SRC}"
        alt="${escapeHtml(BRAND_LOGO_ALT)}"
        width="56"
        height="56"
        decoding="async"
      />
      <div class="croc-cc-top__brand-text">
        <p class="croc-cc-top__product">${escapeHtml(BRAND_WORDMARK)}</p>
        <p class="croc-cc-top__tagline">${escapeHtml(BRAND_TAGLINE)}</p>
      </div>
    </div>
    <nav class="croc-cc-top__nav croc-cc-nav-pill-track" aria-label="${escapeHtml(BRAND_PRODUCT)} sections">
      ${navHtml}
    </nav>
    <div class="croc-cc-top__meta">
      <div class="croc-cc-top__clock glass-panel glass-panel--micro">
        <span class="croc-cc-top__time mono" id="ccWallClock">—</span>
        <span class="croc-cc-top__date" id="ccWallDate">—</span>
      </div>
      <span class="croc-cc-top__tz glass-panel glass-panel--micro" title="All times Malaysia">MYT</span>
      ${healthHtml}
      <button type="button" data-cc-go="incidents" class="croc-cc-bell glass-panel glass-panel--micro" aria-label="Open cases">
        <span class="croc-cc-bell__ico" aria-hidden="true">◉</span>
      </button>
      <div class="croc-cc-user-dd croc-cc-nav-dd" data-cc-nav-dd data-cc-user-dd>
        <button
          type="button"
          class="croc-cc-user croc-cc-nav-dd__trigger glass-panel glass-panel--micro"
          aria-expanded="false"
          aria-haspopup="true"
          aria-label="Account menu"
        >
          <span class="croc-cc-user__av">${escapeHtml(initials(user))}</span>
          <span class="croc-cc-user__text">
            <span class="croc-cc-user__name">${user}</span>
            <span class="croc-cc-user__role">${role}</span>
          </span>
          <span class="croc-cc-nav-dd__chev croc-cc-user-dd__chev" aria-hidden="true">▾</span>
        </button>
        <div class="croc-cc-nav-dd__menu croc-cc-user-dd__menu glass-panel glass-panel--float" role="menu" hidden>
          <div class="croc-cc-user-dd__head" role="none">
            <span class="croc-cc-user-dd__head-name">${user}</span>
            <span class="croc-cc-user-dd__head-role">${role}</span>
          </div>
          <button type="button" data-cc-go="account" class="croc-cc-nav-dd__item" role="menuitem">
            <span class="croc-cc-nav-dd__ico" aria-hidden="true">◍</span>
            <span class="croc-cc-nav-dd__label">Account</span>
          </button>
          <button type="button" data-cc-go="settings" class="croc-cc-nav-dd__item" role="menuitem">
            <span class="croc-cc-nav-dd__ico" aria-hidden="true">⚙</span>
            <span class="croc-cc-nav-dd__label">Settings</span>
          </button>
          <button type="button" class="croc-cc-nav-dd__item croc-cc-user-dd__logout" data-cc-logout role="menuitem">
            <span class="croc-cc-nav-dd__ico" aria-hidden="true">⎋</span>
            <span class="croc-cc-nav-dd__label">Log out</span>
          </button>
        </div>
      </div>
    </div>
  </header>`;
}

/**
 * Sub-route shell: shared CC header + scrollable content slot (router mounts route HTML into slot).
 * @param {{ me?: object, systemHealth?: number, hasRole?: (min: string) => boolean, can?: (cap: string) => boolean, currentHash?: string }} ctx
 */
export function buildCommandCenterPageShellHtml(ctx = {}) {
  return `
<div class="croc-cc croc-cc--page" id="crocCcRoot">
  <div class="croc-cc-ambient" aria-hidden="true"></div>
  ${buildCommandCenterHeaderHtml(ctx)}
  <main class="croc-cc-page-content croc-cc-scroll" id="crocCcPageContent" role="main"></main>
</div>`;
}

/**
 * @param {ParentNode} view
 * @param {{ me?: object, systemHealth?: number, hasRole?: (min: string) => boolean, can?: (cap: string) => boolean, currentHash?: string }} ctx
 * @returns {HTMLElement | null}
 */
export function mountCommandCenterPageShell(view, ctx = {}) {
  activateOverviewCommandCenterLayout();
  try {
    document.body.dataset.ccPage = "1";
  } catch (_) {}
  setChildMarkup(view, buildCommandCenterPageShellHtml(ctx));
  const slot = view.querySelector("#crocCcPageContent");
  if (typeof window !== "undefined") {
    window.__ccPageContentSlot = slot || null;
  }
  return slot;
}

export function clearCommandCenterPageShell() {
  if (typeof window !== "undefined") {
    delete window.__ccPageContentSlot;
  }
  try {
    delete document.body.dataset.ccPage;
  } catch (_) {}
}

/**
 * @param {{ me?: object, kpi?: object, devices?: object[], totalDevices?: number, onlineDevices?: number, systemHealth?: number, hasRole?: (min: string) => boolean, can?: (cap: string) => boolean, currentHash?: string }} ctx
 */
export function buildOverviewCommandCenterShellHtml(ctx = {}) {
  const kpi = ctx.kpi || {};
  const devices = ctx.devices || [];
  const total = ctx.totalDevices != null ? ctx.totalDevices : (kpi.devices != null ? kpi.devices : devices.length);
  const online = ctx.onlineDevices != null ? ctx.onlineDevices : (kpi.online != null ? kpi.online : 0);
  const _offline = Math.max(0, Number(total) - Number(online));
  const healthPct =
    ctx.systemHealth != null && Number.isFinite(Number(ctx.systemHealth))
      ? Math.round(Number(ctx.systemHealth))
      : Number(total) > 0
        ? Math.round((100 * Number(online)) / Number(total))
        : null;
  const _healthLabel = healthPct != null ? `${healthPct}%` : "—";
  const incidentCards = Array.isArray(ctx.incidentCards) ? ctx.incidentCards : [];
  const responders = Array.isArray(ctx.responders) ? ctx.responders : [];
  const timelineItems = Array.isArray(ctx.timelineItems) ? ctx.timelineItems : [];
  const mapEnabled = !!ctx.mapEnabled;
  const me = ctx.me || {};
  const isSaOp = String(me.role || "") === "superadmin" || String(me.role || "") === "sa_staff";
  // Home prefers real units; practice soak stays off unless operator opts in.
  let homeShowPractice = false;
  try {
    homeShowPractice = localStorage.getItem("croc_home_show_practice") === "1";
  } catch (_) {}
  const fleetDevices = (Array.isArray(devices) ? devices : []).filter((d) => {
    const id = String((d && d.device_id) || "").trim();
    if (!id) return false;
    if (!homeShowPractice && isHarnessDeviceId(id)) return false;
    return true;
  });
  const fleetTotal = fleetDevices.length;
  const fleetOnline = fleetDevices.filter((d) => deviceOnline(d)).length;
  const showTotal = fleetTotal;
  const showOnline = fleetOnline;
  const showOffline = Math.max(0, Number(showTotal) - Number(showOnline));
  const showHealthPct =
    Number(showTotal) > 0 ? Math.round((100 * Number(showOnline)) / Number(showTotal)) : null;
  // Primary metric is count (operator language); % stays secondary elsewhere.
  const showHealthLabel =
    Number(showTotal) > 0 ? `${showOnline} of ${showTotal}` : "—";
  const mapHint = mapEnabled
    ? "Malaysia-first · devices & people when shared"
    : isSaOp
      ? "Not available for staff on this site"
      : "Not included for this account";
  const mapEmpty = mapEnabled
    ? "Opening Home map…"
    : isSaOp
      ? "Home map is not open for staff accounts here yet. Ask your team lead if you need map access."
      : "Home map is not available for this account. Contact your administrator if you need access.";

  const landingMode = (() => {
    try {
      const pref = getHomeLandingPreference();
      if (pref === "map" && mapEnabled) return "map";
    } catch (_) {}
    return "overview";
  })();

  return `
<div class="croc-cc croc-cc--v2" id="crocCcRoot" data-cc-landing="${landingMode}">
  <div class="croc-cc-ambient" aria-hidden="true"></div>
  ${buildCommandCenterHeaderHtml({
    ...ctx,
    systemHealth: showHealthPct,
    onlineDevices: showOnline,
    totalDevices: showTotal,
  })}

  ${buildHomeWorkbenchHtml(incidentCards, responders, timelineItems, null, {
    isAdmin: typeof ctx.hasRole === "function" ? !!ctx.hasRole("admin") : false,
  })}

  <div class="croc-cc-main-grid" data-cc-map-stage="1">
    <aside class="croc-cc-left glass-panel glass-panel--panel glass-panel--float croc-cc-scroll" data-cc-float="devices" data-float-open="0">
      <div class="croc-cc-panel-head">
        <h3 class="croc-cc-panel-title">Devices &amp; zones</h3>
        <div class="croc-cc-panel-head__trail">
          <span class="croc-cc-panel-badge">Live</span>
          <button type="button" class="btn sm ghost btn-tap croc-cc-float-toggle" data-float-toggle="devices" aria-expanded="false" title="Fold devices">Fold</button>
        </div>
      </div>
      <button type="button" class="croc-cc-float-tab" data-float-tab="devices" aria-expanded="false" title="Open boards">Boards</button>
      <label class="croc-cc-search glass-panel glass-panel--inset">
        <span class="croc-cc-search__ico" aria-hidden="true">⌕</span>
        <input type="search" id="ccAssetSearch" placeholder="Search devices, zones…" autocomplete="off" />
      </label>
      <div class="croc-cc-filters" role="group" aria-label="Asset filters">
        <button type="button" class="croc-cc-pill croc-cc-pill--on glass-panel glass-panel--micro" data-cc-filter="all">All <span id="ccFilterAll">${showTotal}</span></button>
        <button type="button" class="croc-cc-pill glass-panel glass-panel--micro" data-cc-filter="online">Online <span id="ccFilterOnline">${showOnline}</span></button>
        <button type="button" class="croc-cc-pill glass-panel glass-panel--micro" data-cc-filter="offline">Offline <span id="ccFilterOffline">${showOffline}</span></button>
      </div>
      <div class="croc-cc-zones croc-cc-scroll">${buildZoneRows(fleetDevices)}</div>
      <div class="croc-cc-assets croc-cc-scroll" id="ccAssetList">${buildAssetRows(fleetDevices)}</div>
    </aside>

    <section class="croc-cc-map-wrap glass-panel glass-panel--map" aria-label="Home map">
      <button type="button" class="croc-cc-map-tb-chip btn-tap" data-map-tb-tab="1" aria-expanded="false" title="Open map tools">Map tools</button>
      <div class="croc-cc-map-toolbar glass-panel glass-panel--float croc-cc-map-toolbar--frost" data-map-tb-open="1">
        <div class="croc-cc-map-toolbar__lead">
          <div class="croc-landing-pick-wrap croc-landing-pick-wrap--hub" title="Home focus">
            <div class="croc-landing-pick croc-landing-pick--maphead glass-panel glass-panel--micro" id="croc-overview-landing-pick" role="group" aria-label="Overview or map focus">
              <button type="button" class="croc-landing-pick__btn" data-croc-landing="overview" aria-pressed="false" title="Home">Home</button>
              <button type="button" class="croc-landing-pick__btn" data-croc-landing="map" aria-pressed="false" title="${mapEnabled ? "Map" : "Map — not available yet"}" ${mapEnabled ? "" : "disabled aria-disabled=\"true\""}>Map</button>
            </div>
          </div>
          <div class="croc-cc-map-toolbar__titles">
            <p class="croc-cc-map-toolbar__title">Home map</p>
            <p class="muted croc-cc-map-toolbar__hint">${mapHint}</p>
          </div>
        </div>
        <div class="croc-cc-map-toolbar__trail">
          <article class="croc-cc-net-widget croc-cc-net-widget--inline glass-panel glass-panel--micro" aria-label="Devices online">
            <p class="croc-cc-net-widget__k">Devices online</p>
            <p class="croc-cc-net-widget__v mono" id="ccNetHealth">${showHealthLabel}</p>
            <p class="croc-cc-net-widget__s">${fmtDevicesOnlineOfTotal(showOnline, showTotal)}</p>
          </article>
          <div id="ccSoftSoftMapChip" class="croc-soft-mapchip-host" data-soft-mapchip-host="1" title="Soft Soft product lanes (not Live Deep)">
            ${softLanesMapChipHtml(null)}
          </div>
          <button type="button" class="croc-cc-link croc-cc-map-dispatch-link" data-cc-go="response" data-cc-id="siren">Siren</button>
          <button type="button" class="btn sm ghost btn-tap croc-cc-map-night-toggle" data-map-night-toggle="1" aria-pressed="false" title="Dark satellite (ops)">Night</button>
          <button type="button" class="btn sm ghost btn-tap croc-cc-map-3d-toggle" data-map-3d-toggle="1" aria-pressed="false" title="Toggle 3D map">3D map</button>
          <button type="button" class="btn sm ghost btn-tap croc-cc-map-toolbar__fold" data-map-tb-toggle="1" aria-expanded="true" title="Fold map bar">Fold</button>
        </div>
      </div>
      <div class="croc-cc-map-stage${mapEnabled ? " croc-cc-map-stage--armed" : ""}">
        <div class="croc-cc-map-bg" aria-hidden="true"></div>
        <div class="croc-cc-map-vignette" aria-hidden="true"></div>
        <div class="croc-cc-map-depth" aria-hidden="true"></div>
        <div class="croc-cc-map-glow croc-cc-map-glow--blue" aria-hidden="true"></div>
        <div class="croc-cc-map-glow croc-cc-map-glow--violet" aria-hidden="true"></div>
        ${mapEnabled ? '<div id="cc-overview-map-canvas" class="croc-cc-map-canvas" aria-label="Home map"></div>' : ""}
        <p class="muted croc-cc-map-placeholder">
          ${mapEmpty}
        </p>
      </div>
      <div class="croc-ai-stage-host" data-ai-stage-host="1">
        ${buildMapAiAlertStageHtml(null, incidentCards)}
      </div>
    </section>

    <aside class="croc-cc-right glass-panel glass-panel--panel glass-panel--float" data-cc-float="feed" data-float-open="0" aria-label="Incident feed dock">
      <div class="croc-cc-right__head croc-cc-panel-head">
        <div>
          <p class="croc-cc-panel-kicker">Live</p>
          <h3 class="croc-cc-panel-title">Incident feed</h3>
          <p class="croc-cc-panel-sub">Live alarms · pulse · busy sites</p>
        </div>
        <div class="croc-cc-panel-head__trail">
          ${ccGoBtn("incidents", "View all", "croc-cc-link")}
          <button type="button" class="btn sm ghost btn-tap croc-cc-float-toggle" data-float-toggle="feed" aria-expanded="false" title="Fold feed">Fold</button>
        </div>
      </div>
      <button type="button" class="croc-cc-float-tab" data-float-tab="feed" aria-expanded="false" title="Open incident feed">Feed</button>
      <div class="croc-cc-right__stack">
        <section class="croc-cc-right__block croc-cc-right__block--live" aria-label="Live alarms">
          <header class="croc-cc-right__block-head">
            <h4 class="croc-cc-right__block-title">Live</h4>
            <span class="croc-cc-panel-badge" id="ccFeedLiveCount">${incidentCards.length}</span>
          </header>
          <div class="croc-cc-feed croc-cc-scroll">${buildIncidentCards(incidentCards)}</div>
        </section>
        <section class="croc-cc-right__block croc-cc-right__block--pulse glass-panel glass-panel--inset" id="ccAlarmSummary" aria-label="Alarm pulse">
          <header class="croc-cc-right__block-head">
            <h4 class="croc-cc-right__block-title">Pulse</h4>
          </header>
          <div class="croc-cc-dispatch-queue">
            <span class="croc-cc-dispatch-queue__item croc-cc-dispatch-queue__item--crit">24h <strong class="mono" id="ccAlarms24h">—</strong></span>
            <span class="croc-cc-dispatch-queue__item croc-cc-dispatch-queue__item--high">7d <strong class="mono" id="ccAlarms7d">—</strong></span>
          </div>
        </section>
        <section class="croc-cc-right__block croc-cc-right__block--sites glass-panel glass-panel--inset" id="ccAlarmTopSources" aria-label="Busy sites">
          <header class="croc-cc-right__block-head">
            <h4 class="croc-cc-right__block-title">Busy sites</h4>
            <span class="muted croc-cc-right__block-meta">7d</span>
          </header>
          <div class="croc-cc-alarm-sources__list muted" id="ccAlarmTopSourcesList">Loading…</div>
        </section>
      </div>
    </aside>
  </div>

  <div class="croc-cc-loop-band" hidden aria-hidden="true"></div>

  <div class="croc-cc-status-bar glass-panel glass-panel--status">
    <span>Devices <strong id="ccStatTotal">${showTotal}</strong></span>
    <span>Online <strong id="ccStatOnline">${showOnline}</strong></span>
    <span>Open alarms <strong id="ccStatIncidents">${incidentCards.length}</strong></span>
    <span>Device health <strong id="ccStatHealth">${showHealthLabel}</strong></span>
  </div>
</div>`;
}

export function activateOverviewCommandCenterLayout() {
  try {
    document.body.dataset.overviewCc = "1";
    document.documentElement.dataset.theme = "light";
    // Belt-and-suspenders: classic shell chrome must not sit beside CC (dual-nav overlap).
    for (const sel of [".sidebar", ".sidebar-backdrop", ".topbar", ".site-footer--shell", "#sidebarBackdrop"]) {
      const el = document.querySelector(sel);
      if (!el) continue;
      el.setAttribute("hidden", "");
      el.setAttribute("aria-hidden", "true");
      el.style.setProperty("display", "none", "important");
    }
    applyHomeLandingChrome(document.getElementById("crocCcRoot"), null);
  } catch (_) {}
}

export function deactivateOverviewCommandCenterLayout() {
  clearCommandCenterPageShell();
  try {
    delete document.body.dataset.overviewCc;
    delete document.body.dataset.ccLanding;
    for (const sel of [".sidebar", ".topbar", ".site-footer--shell"]) {
      const el = document.querySelector(sel);
      if (!el) continue;
      el.removeAttribute("hidden");
      el.removeAttribute("aria-hidden");
      el.style.removeProperty("display");
    }
    const saved = localStorage.getItem(LS.theme);
    const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    if (typeof setTheme === "function") {
      setTheme(saved || (prefersDark ? "dark" : "light"));
    }
  } catch (_) {}
}

/**
 * @param {ParentNode} view
 * @param {{
 *   devices?: object[],
 *   apiOr?: Function,
 *   buildAlarmsSummaryPath?: Function,
 *   alarmsSummaryTimeoutMs?: number,
 * }} [ctx]
 */
export function wireOverviewCommandCenterChrome(view, ctx = {}) {
  if (typeof window.__overviewCcDispose === "function") {
    try {
      window.__overviewCcDispose();
    } catch (_) {}
  }
  // Sub-route wire() runs after mountCommandCenterPageShell; re-apply layout tokens so
  // portaled dropdowns on document.body inherit --cc-blur* (dispose must not strip these).
  if (view.querySelector(".croc-cc-top, .croc-cc")) {
    activateOverviewCommandCenterLayout();
  }
  try {
    applyHomeLandingChrome(view.querySelector("#crocCcRoot") || view, ctx.me);
  } catch (_) {}
  try {
    wireCcGoClicks(view);
  } catch (_) {}

  const wb = view.querySelector("[data-home-wb]");
  const WB_PREF_KEY = "croc_home_wb_open";
  const WB_DOCK_KEY = "croc_home_wb_dock";
  const MAP_TB_KEY = "croc_home_map_tb_open";
  const syncWbChrome = () => {
    if (!wb) return;
    const open = wb.getAttribute("data-wb-open") === "1";
    const docked = wb.getAttribute("data-wb-dock") === "1";
    const toggleBtn = wb.querySelector("[data-wb-toggle]");
    const dockBtn = wb.querySelector("[data-wb-dock-toggle]");
    const chip = wb.querySelector("[data-wb-dock-tab]");
    if (toggleBtn) {
      toggleBtn.setAttribute("aria-expanded", open ? "true" : "false");
      toggleBtn.textContent = open ? "Hide" : "Workflow";
    }
    if (dockBtn) {
      dockBtn.setAttribute("aria-expanded", docked ? "false" : "true");
      dockBtn.textContent = docked ? "Open" : "Fold";
    }
    if (chip) chip.setAttribute("aria-expanded", docked ? "false" : "true");
  };
  try {
    const pref = localStorage.getItem(WB_PREF_KEY);
    if (wb && (pref === "0" || pref === "1")) {
      wb.setAttribute("data-wb-open", pref === "1" ? "1" : "0");
    }
    const dockPref = localStorage.getItem(WB_DOCK_KEY);
    if (wb && (dockPref === "0" || dockPref === "1")) {
      wb.setAttribute("data-wb-dock", dockPref === "1" ? "1" : "0");
    } else if (wb && document.body.dataset.ccLanding === "map") {
      // Map big-screen default: dock the strip so the map owns the viewport.
      wb.setAttribute("data-wb-dock", "1");
    }
    // tip 505 / tip595: map landing always starts desks folded so map pins stay clickable.
    // Sticky open pref is restored only after the operator taps Workflow (toggle writes LS).
    if (wb && document.body.dataset.ccLanding === "map") {
      wb.setAttribute("data-wb-open", "0");
      if (dockPref !== "0") wb.setAttribute("data-wb-dock", "1");
    }
    syncWbChrome();
  } catch (_) {}
  const iosWf = view.querySelector(".croc-ios-wf--map-rail");
  const syncIosRail = () => {
    if (!iosWf) return;
    const on = iosWf.getAttribute("data-ios-expanded") === "1";
    const btn = iosWf.querySelector("[data-ios-expand]");
    if (btn) {
      btn.setAttribute("aria-expanded", on ? "true" : "false");
      btn.textContent = on ? "Hide details" : "Loop details";
    }
  };
  const onIosRail = (ev) => {
    const btn = ev.target && ev.target.closest && ev.target.closest("[data-ios-expand]");
    if (!btn || !iosWf || !iosWf.contains(btn)) return;
    ev.preventDefault();
    const on = iosWf.getAttribute("data-ios-expanded") !== "1";
    iosWf.setAttribute("data-ios-expanded", on ? "1" : "0");
    syncIosRail();
  };
  if (iosWf) {
    iosWf.addEventListener("click", onIosRail);
    syncIosRail();
  }
  const onAiStageClick = (ev) => {
    const previewOn = ev.target && ev.target.closest && ev.target.closest('[data-ai-stage-preview="1"]');
    const previewOff = ev.target && ev.target.closest && ev.target.closest('[data-ai-stage-preview="0"]');
    const dismiss = ev.target && ev.target.closest && ev.target.closest("[data-ai-stage-dismiss]");
    const openWb = ev.target && ev.target.closest && ev.target.closest("[data-ai-open-wb]");
    const openModal = ev.target && ev.target.closest && ev.target.closest("[data-ai-open-modal]");
    const closeModal = ev.target && ev.target.closest && ev.target.closest("[data-ai-modal-close]");
    const host = view.querySelector("[data-ai-stage-host]");
    if (!host) return;
    if (openModal && host.contains(openModal)) {
      ev.preventDefault();
      host.removeAttribute("data-ai-snooze-id");
      setAiMapModalOpen(host, true);
      return;
    }
    if (closeModal && host.contains(closeModal)) {
      ev.preventDefault();
      // Soft preview close; live interrupt "Later" uses dismiss path below
      if (host.querySelector('[data-ai-stage-preview-on="1"]')) {
        host.removeAttribute("data-ai-preview");
        patchMapAiAlertStage(view, view.__ccAiPack || null, view.__ccIncidentCards || [], { preview: false, modalOpen: false });
        return;
      }
      const mapFocus = host.querySelector("[data-ai-map-focus], [data-ds-incident]");
      const snooze = mapFocus
        ? String(mapFocus.getAttribute("data-ai-map-focus") || mapFocus.getAttribute("data-ds-incident") || "").trim()
        : "";
      if (snooze) host.setAttribute("data-ai-snooze-id", snooze);
      else host.setAttribute("data-ai-snooze-id", "*");
      host.removeAttribute("data-ai-modal-keep");
      setAiMapModalOpen(host, false);
      return;
    }
    if (openWb && host.contains(openWb) && wb) {
      ev.preventDefault();
      wb.setAttribute("data-wb-dock", "0");
      wb.setAttribute("data-wb-open", "1");
      try {
        localStorage.setItem(WB_DOCK_KEY, "0");
        localStorage.setItem(WB_PREF_KEY, "1");
      } catch (_) {}
      syncWbChrome();
      try { window.dispatchEvent(new Event("resize")); } catch (_) {}
      return;
    }
    if (previewOn && host.contains(previewOn)) {
      ev.preventDefault();
      host.setAttribute("data-ai-preview", "1");
      patchMapAiAlertStage(view, view.__ccAiPack || null, view.__ccIncidentCards || [], { preview: true, modalOpen: true });
      return;
    }
    if ((previewOff || dismiss) && host.contains(previewOff || dismiss)) {
      ev.preventDefault();
      host.removeAttribute("data-ai-preview");
      host.removeAttribute("data-ai-modal-keep");
      const focus = (dismiss || previewOff).closest("[data-ai-stage]");
      const mapFocus = host.querySelector("[data-ai-map-focus], [data-ds-incident]");
      const snooze = mapFocus
        ? String(mapFocus.getAttribute("data-ai-map-focus") || mapFocus.getAttribute("data-ds-incident") || "").trim()
        : "";
      if (snooze) host.setAttribute("data-ai-snooze-id", snooze);
      else host.setAttribute("data-ai-snooze-id", "*");
      void focus;
      patchMapAiAlertStage(view, view.__ccAiPack || null, view.__ccIncidentCards || [], { preview: false, modalOpen: false });
    }
  };
  view.addEventListener("click", onAiStageClick);
  try {
    patchMapAiAlertStage(view, view.__ccAiPack || null, []);
  } catch (_) {}
  const onWbToggle = (ev) => {
    if (!wb) return;
    const dockTab = ev.target && ev.target.closest && ev.target.closest("[data-wb-dock-tab]");
    const dockToggle = ev.target && ev.target.closest && ev.target.closest("[data-wb-dock-toggle]");
    const desksToggle = ev.target && ev.target.closest && ev.target.closest("[data-wb-toggle]");
    if (dockTab && wb.contains(dockTab)) {
      wb.setAttribute("data-wb-dock", "0");
      try { localStorage.setItem(WB_DOCK_KEY, "0"); } catch (_) {}
      syncWbChrome();
      try { window.dispatchEvent(new Event("resize")); } catch (_) {}
      return;
    }
    if (dockToggle && wb.contains(dockToggle)) {
      const docked = wb.getAttribute("data-wb-dock") !== "1";
      wb.setAttribute("data-wb-dock", docked ? "1" : "0");
      if (docked) wb.setAttribute("data-wb-open", "0");
      try {
        localStorage.setItem(WB_DOCK_KEY, docked ? "1" : "0");
        if (docked) localStorage.setItem(WB_PREF_KEY, "0");
      } catch (_) {}
      syncWbChrome();
      try { window.dispatchEvent(new Event("resize")); } catch (_) {}
      return;
    }
    if (!desksToggle || !wb.contains(desksToggle)) return;
    wb.setAttribute("data-wb-dock", "0");
    const open = wb.getAttribute("data-wb-open") !== "1";
    wb.setAttribute("data-wb-open", open ? "1" : "0");
    try {
      localStorage.setItem(WB_PREF_KEY, open ? "1" : "0");
      localStorage.setItem(WB_DOCK_KEY, "0");
    } catch (_) {}
    syncWbChrome();
    try { window.dispatchEvent(new Event("resize")); } catch (_) {}
  };
  if (wb) wb.addEventListener("click", onWbToggle);

  const mapTb = view.querySelector(".croc-cc-map-toolbar");
  const mapWrap = view.querySelector(".croc-cc-map-wrap");
  const syncMapTb = () => {
    if (!mapTb || !mapWrap) return;
    const open = mapTb.getAttribute("data-map-tb-open") !== "0";
    mapWrap.setAttribute("data-map-tb-open", open ? "1" : "0");
    const foldBtn = mapTb.querySelector("[data-map-tb-toggle]");
    const chip = mapWrap.querySelector("[data-map-tb-tab]");
    if (foldBtn) {
      foldBtn.setAttribute("aria-expanded", open ? "true" : "false");
      foldBtn.textContent = open ? "Fold" : "Open";
    }
    if (chip) chip.setAttribute("aria-expanded", open ? "true" : "false");
  };
  try {
    const tbPref = localStorage.getItem(MAP_TB_KEY);
    if (mapTb && (tbPref === "0" || tbPref === "1")) {
      mapTb.setAttribute("data-map-tb-open", tbPref);
    }
    syncMapTb();
  } catch (_) {}
  const onMapTbToggle = (ev) => {
    if (!mapTb || !mapWrap) return;
    const tab = ev.target && ev.target.closest && ev.target.closest("[data-map-tb-tab]");
    const fold = ev.target && ev.target.closest && ev.target.closest("[data-map-tb-toggle]");
    if (tab && mapWrap.contains(tab)) {
      mapTb.setAttribute("data-map-tb-open", "1");
      try { localStorage.setItem(MAP_TB_KEY, "1"); } catch (_) {}
      syncMapTb();
      return;
    }
    if (!fold || !mapTb.contains(fold)) return;
    const open = mapTb.getAttribute("data-map-tb-open") !== "1";
    mapTb.setAttribute("data-map-tb-open", open ? "1" : "0");
    try { localStorage.setItem(MAP_TB_KEY, open ? "1" : "0"); } catch (_) {}
    syncMapTb();
  };
  if (mapWrap) mapWrap.addEventListener("click", onMapTbToggle);

  const MAP_3D_KEY = "croc_home_map_3d";
  const syncMap3dBtn = (on) => {
    const btn = view.querySelector("[data-map-3d-toggle]");
    if (!btn) return;
    btn.setAttribute("aria-pressed", on ? "true" : "false");
    btn.textContent = on ? "3D on" : "3D map";
  };
  const applyMap3d = (on) => {
    const map = typeof window !== "undefined" ? window.__crocCcMap : null;
    if (map) {
      try {
        setCommandCenterMap3d(map, !!on);
      } catch (_) {}
    }
    syncMap3dBtn(!!on);
  };
  try {
    const pref3d = localStorage.getItem(MAP_3D_KEY) === "1";
    syncMap3dBtn(pref3d);
    if (pref3d) {
      // Map may mount after chrome wire — retry briefly.
      let n = 0;
      const t = setInterval(() => {
        n += 1;
        if (window.__crocCcMap || n > 20) {
          clearInterval(t);
          applyMap3d(true);
        }
      }, 250);
    }
  } catch (_) {}
  const onMap3dToggle = (ev) => {
    const btn = ev.target && ev.target.closest && ev.target.closest("[data-map-3d-toggle]");
    if (!btn || !view.contains(btn)) return;
    const on = btn.getAttribute("aria-pressed") !== "true";
    try {
      localStorage.setItem(MAP_3D_KEY, on ? "1" : "0");
    } catch (_) {}
    applyMap3d(on);
  };
  if (mapWrap) mapWrap.addEventListener("click", onMap3dToggle);

  const MAP_NIGHT_KEY = "croc_home_map_night";
  const syncMapNightBtn = (on) => {
    const btn = view.querySelector("[data-map-night-toggle]");
    if (!btn) return;
    btn.setAttribute("aria-pressed", on ? "true" : "false");
    btn.textContent = on ? "Night on" : "Night";
    btn.classList.toggle("is-on", !!on);
  };
  const applyMapNight = (on) => {
    const map = typeof window !== "undefined" ? window.__crocCcMap : null;
    if (map) {
      try {
        void setCommandCenterMapNight(map, !!on);
      } catch (_) {}
    }
    syncMapNightBtn(!!on);
    try {
      document.body.dataset.ccMapNight = on ? "1" : "0";
    } catch (_) {}
  };
  try {
    const rawNight = localStorage.getItem(MAP_NIGHT_KEY);
    // Default ON (dark satellite) when unset — FH2-like ops look.
    const prefNight = rawNight == null ? true : rawNight === "1";
    syncMapNightBtn(prefNight);
    if (prefNight) {
      let n = 0;
      const t = setInterval(() => {
        n += 1;
        if (window.__crocCcMap || n > 20) {
          clearInterval(t);
          applyMapNight(true);
        }
      }, 250);
    }
  } catch (_) {}
  const onMapNightToggle = (ev) => {
    const btn = ev.target && ev.target.closest && ev.target.closest("[data-map-night-toggle]");
    if (!btn || !view.contains(btn)) return;
    const on = btn.getAttribute("aria-pressed") !== "true";
    try {
      localStorage.setItem(MAP_NIGHT_KEY, on ? "1" : "0");
    } catch (_) {}
    applyMapNight(on);
  };
  if (mapWrap) mapWrap.addEventListener("click", onMapNightToggle);

  const FLOAT_PREF = {
    devices: "croc_home_float_devices",
    feed: "croc_home_float_feed",
  };
  const setFloatOpen = (key, open) => {
    const panel = view.querySelector(`[data-cc-float="${key}"]`);
    if (!panel) return;
    panel.setAttribute("data-float-open", open ? "1" : "0");
    const toggles = panel.querySelectorAll(`[data-float-toggle="${key}"], [data-float-tab="${key}"]`);
    toggles.forEach((el) => {
      el.setAttribute("aria-expanded", open ? "true" : "false");
      if (el.matches("[data-float-toggle]")) el.textContent = open ? "Fold" : "Open";
    });
    try { localStorage.setItem(FLOAT_PREF[key], open ? "1" : "0"); } catch (_) {}
    try { window.dispatchEvent(new Event("resize")); } catch (_) {}
  };
  ["devices", "feed"].forEach((key) => {
    let pref = null;
    try { pref = localStorage.getItem(FLOAT_PREF[key]); } catch (_) {}
    // Map landing: start folded so the map stays the hero; overview restores prefs.
    const landing = String((view.querySelector("#crocCcRoot") || {}).getAttribute?.("data-cc-landing") || document.body.dataset.ccLanding || "map");
    const open = landing === "overview"
      ? (pref !== "0")
      : (pref === "1");
    setFloatOpen(key, open);
  });
  const onFloatToggle = (ev) => {
    const btn = ev.target && ev.target.closest && ev.target.closest("[data-float-toggle], [data-float-tab]");
    if (!btn) return;
    const key = btn.getAttribute("data-float-toggle") || btn.getAttribute("data-float-tab");
    if (!key || !FLOAT_PREF[key]) return;
    const panel = view.querySelector(`[data-cc-float="${key}"]`);
    if (!panel) return;
    const open = panel.getAttribute("data-float-open") !== "1";
    setFloatOpen(key, open);
  };
  view.addEventListener("click", onFloatToggle);

  const devices = Array.isArray(ctx.devices) ? ctx.devices : [];
  let clockTimer = 0;

  const tickClock = () => {
    const now = new Date();
    const timeEl = view.querySelector("#ccWallClock");
    const dateEl = view.querySelector("#ccWallDate");
    if (timeEl) {
      timeEl.textContent = now.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone: "Asia/Kuala_Lumpur",
      });
    }
    if (dateEl) {
      dateEl.textContent = now.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kuala_Lumpur",
      });
    }
  };
  tickClock();
  clockTimer = window.setInterval(tickClock, 1000);

  const patchAlarmSummary = (summary) => {
    if (!summary || typeof summary !== "object") return;
    const h24 = view.querySelector("#ccAlarms24h");
    const h7 = view.querySelector("#ccAlarms7d");
    const listEl = view.querySelector("#ccAlarmTopSourcesList");
    if (h24) h24.textContent = String(summary.last_24h != null ? summary.last_24h : "—");
    if (h7) h7.textContent = String(summary.last_7d != null ? summary.last_7d : "—");
    if (!listEl) return;
    const top = Array.isArray(summary.top_sources_7d) ? summary.top_sources_7d : [];
    if (!top.length) {
      listEl.textContent = "Quiet week — no busy sites yet.";
      return;
    }
    listEl.innerHTML = top
      .slice(0, 6)
      .map((row) => {
        const soft = softDeviceLabel(row.source_id);
        return `<div class="croc-cc-alarm-sources__row"><span>${escapeHtml(soft)}</span><span class="mono">${escapeHtml(String(row.c != null ? row.c : ""))}</span></div>`;
      })
      .join("");
  };

  if (typeof ctx.apiOr === "function" && typeof ctx.buildAlarmsSummaryPath === "function") {
    void ctx.apiOr(ctx.buildAlarmsSummaryPath(), null, {
      timeoutMs: Number(ctx.alarmsSummaryTimeoutMs || 16000),
    })
      .then((d) => {
        if (d) patchAlarmSummary(d);
      })
      .catch(() => {});
  }

  const search = view.querySelector("#ccAssetSearch");
  const assetList = view.querySelector("#ccAssetList");
  if (search && assetList) {
    const onSearch = () => {
      const q = String(search.value || "").trim().toLowerCase();
      const rows = assetList.querySelectorAll(".croc-cc-asset");
      rows.forEach((row) => {
        const txt = (row.textContent || "").toLowerCase();
        row.style.display = !q || txt.includes(q) ? "" : "none";
      });
    };
    search.addEventListener("input", onSearch);
    window.__overviewCcDispose = () => {
      if (clockTimer) clearInterval(clockTimer);
      search.removeEventListener("input", onSearch);
      if (wb) wb.removeEventListener("click", onWbToggle);
      if (iosWf) iosWf.removeEventListener("click", onIosRail);
      if (mapWrap) mapWrap.removeEventListener("click", onMapTbToggle);
      try { view.removeEventListener("click", onAiStageClick); } catch (_) {}
      try { view.removeEventListener("click", onFloatToggle); } catch (_) {}
      try { delete document.body.dataset.ccAiAlert; } catch (_) {}
    };
  } else {
    window.__overviewCcDispose = () => {
      if (clockTimer) clearInterval(clockTimer);
      if (wb) wb.removeEventListener("click", onWbToggle);
      if (iosWf) iosWf.removeEventListener("click", onIosRail);
      if (mapWrap) mapWrap.removeEventListener("click", onMapTbToggle);
      try { view.removeEventListener("click", onAiStageClick); } catch (_) {}
      try { view.removeEventListener("click", onFloatToggle); } catch (_) {}
      try { delete document.body.dataset.ccAiAlert; } catch (_) {}
    };
  }

  view.querySelectorAll(".croc-cc-pill[data-cc-filter]").forEach((btn) => {
    btn.addEventListener("click", () => {
      view.querySelectorAll(".croc-cc-pill").forEach((b) => b.classList.remove("croc-cc-pill--on"));
      btn.classList.add("croc-cc-pill--on");
      const mode = btn.getAttribute("data-cc-filter");
      if (!assetList) return;
      assetList.querySelectorAll(".croc-cc-asset").forEach((row) => {
        const on = row.querySelector(".croc-cc-asset__dot--on");
        if (mode === "all") row.style.display = "";
        else if (mode === "online") row.style.display = on ? "" : "none";
        else row.style.display = on ? "none" : "";
      });
    });
  });

  const showPracticeBtn = view.querySelector("#crocCcShowPracticeHome");
  if (showPracticeBtn) {
    showPracticeBtn.addEventListener("click", () => {
      try {
        localStorage.setItem("croc_home_show_practice", "1");
      } catch (_) {}
      try {
        location.hash = "#/";
        if (typeof window.dispatchEvent === "function") {
          window.dispatchEvent(new HashChangeEvent("hashchange"));
        }
      } catch (_) {
        location.reload();
      }
    });
  }

  view.querySelectorAll(".croc-cc-chip").forEach((chip) => {
    chip.addEventListener("click", () => chip.classList.toggle("croc-cc-chip--on"));
  });

  // AbortController: live poll used to re-call wire() and stack click handlers on
  // Home/Operations triggers (open+close cancel → "click does nothing").
  const navAbort = new AbortController();
  const navSignal = navAbort.signal;

  const scrubOrphanPortaledNavMenus = () => {
    try {
      document.querySelectorAll(".croc-cc-nav-dd__menu--portaled").forEach((menu) => {
        try {
          menu.classList.remove("croc-cc-nav-dd__menu--portaled", "croc-cc-nav-dd__menu--anim-in", "is-open");
          menu.hidden = true;
          menu.style.position = "";
          menu.style.left = "";
          menu.style.top = "";
          menu.style.transform = "";
          menu.style.zIndex = "";
          if (menu.parentNode === document.body) menu.remove();
        } catch (_) {}
      });
    } catch (_) {}
  };

  const resolveNavMenu = (wrap) =>
    wrap.__ccNavMenu || wrap.querySelector(".croc-cc-nav-dd__menu");

  const closeAllNavDropdowns = (except) => {
    view.querySelectorAll("[data-cc-nav-dd]").forEach((wrap) => {
      if (except && wrap === except) return;
      closeNavDropdown(wrap);
    });
  };

  const positionPortaledNavMenu = (wrap, menu) => {
    const trigger = wrap.querySelector(".croc-cc-nav-dd__trigger");
    if (!trigger || !menu) return;
    const r = trigger.getBoundingClientRect();
    menu.style.position = "fixed";
    menu.style.top = `${Math.round(r.bottom + 8)}px`;
    menu.style.zIndex = "400";
    if (wrap.hasAttribute("data-cc-user-dd")) {
      const right = Math.min(r.right, window.innerWidth - 8);
      menu.style.left = `${Math.round(right)}px`;
      menu.style.transform = "translateX(-100%)";
      return;
    }
    const halfW = Math.max(110, (menu.offsetWidth || 220) / 2);
    const left = Math.max(halfW + 8, Math.min(r.left + r.width / 2, window.innerWidth - halfW - 8));
    menu.style.left = `${Math.round(left)}px`;
    menu.style.transform = "translateX(-50%)";
  };

  const closeNavDropdown = (wrap) => {
    const trigger = wrap.querySelector(".croc-cc-nav-dd__trigger");
    const menu = resolveNavMenu(wrap);
    if (trigger) trigger.setAttribute("aria-expanded", "false");
    if (menu) {
      menu.classList.remove("croc-cc-nav-dd__menu--anim-in");
      menu.classList.remove("is-open");
      menu.hidden = true;
      menu.classList.remove("croc-cc-nav-dd__menu--portaled");
      menu.style.position = "";
      menu.style.left = "";
      menu.style.top = "";
      menu.style.transform = "";
      menu.style.zIndex = "";
      if (menu.parentNode !== wrap) wrap.appendChild(menu);
      wrap.__ccNavMenu = menu;
    }
    wrap.classList.remove("croc-cc-nav-dd--open");
  };

  const openNavDropdown = (wrap) => {
    const trigger = wrap.querySelector(".croc-cc-nav-dd__trigger");
    const menu = resolveNavMenu(wrap);
    if (!trigger || !menu) return;
    closeAllNavDropdowns(wrap);
    wrap.__ccNavMenu = menu;
    document.body.appendChild(menu);
    menu.classList.add("croc-cc-nav-dd__menu--portaled");
    menu.classList.add("is-open");
    positionPortaledNavMenu(wrap, menu);
    menu.classList.remove("croc-cc-nav-dd__menu--anim-in");
    void menu.offsetWidth;
    menu.classList.add("croc-cc-nav-dd__menu--anim-in");
    menu.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    wrap.classList.add("croc-cc-nav-dd--open");
  };

  const onNavDropdownReflow = () => {
    view.querySelectorAll(".croc-cc-nav-dd--open").forEach((wrap) => {
      const menu = wrap.__ccNavMenu;
      if (menu && !menu.hidden) positionPortaledNavMenu(wrap, menu);
    });
  };

  view.querySelectorAll("[data-cc-nav-dd]").forEach((wrap) => {
    const trigger = wrap.querySelector(".croc-cc-nav-dd__trigger");
    const menu = wrap.querySelector(".croc-cc-nav-dd__menu");
    if (!trigger || !menu) return;
    wrap.__ccNavMenu = menu;
    trigger.addEventListener(
      "click",
      (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const m = resolveNavMenu(wrap);
        if (!m || m.hidden) openNavDropdown(wrap);
        else closeNavDropdown(wrap);
      },
      { signal: navSignal },
    );
  });

  window.addEventListener("resize", onNavDropdownReflow, { signal: navSignal });
  window.addEventListener("scroll", onNavDropdownReflow, { capture: true, signal: navSignal });

  const onDocClick = (ev) => {
    if (ev.target.closest("[data-cc-nav-dd]")) return;
    if (ev.target.closest(".croc-cc-nav-dd__menu--portaled")) return;
    closeAllNavDropdowns();
  };
  const onDocKey = (ev) => {
    if (ev.key === "Escape") closeAllNavDropdowns();
  };
  document.addEventListener("click", onDocClick, { signal: navSignal });
  document.addEventListener("keydown", onDocKey, { signal: navSignal });

  const runConsoleLogout = () => {
    try { closeAllNavDropdowns(); } catch (_) {}
    const hidden = document.getElementById("logoutBtn");
    if (hidden) {
      hidden.click();
      return;
    }
    // Fallback when legacy chrome is absent — clear soft client state only.
    try { location.hash = "#/login"; } catch (_) {}
  };
  const onLogoutClick = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    runConsoleLogout();
  };
  window.__ccRunLogout = runConsoleLogout;
  // Document delegation: portaled user menu lives on body, not always under #view.
  if (!window.__ccLogoutDelegated) {
    window.__ccLogoutDelegated = true;
    document.addEventListener("click", (ev) => {
      const btn = ev.target && ev.target.closest && ev.target.closest("[data-cc-logout]");
      if (!btn) return;
      ev.preventDefault();
      ev.stopPropagation();
      if (typeof window.__ccRunLogout === "function") window.__ccRunLogout();
    }, true);
  }
  view.querySelectorAll("[data-cc-logout]").forEach((btn) => {
    btn.addEventListener("click", onLogoutClick);
  });

  const prevDispose = window.__overviewCcDispose;
  window.__overviewCcDispose = () => {
    try {
      navAbort.abort();
    } catch (_) {}
    view.querySelectorAll("[data-cc-nav-dd]").forEach((wrap) => closeNavDropdown(wrap));
    scrubOrphanPortaledNavMenus();
    view.querySelectorAll("[data-cc-logout]").forEach((btn) => {
      btn.removeEventListener("click", onLogoutClick);
    });
    if (typeof prevDispose === "function") prevDispose();
  };

  void devices;
}

/**
 * @param {ParentNode} view
 * @param {{ devices?: number, online?: number, offline?: number, health?: number }} vals
 */
export function patchOverviewCommandCenterMetrics(view, vals = {}) {
  const set = (id, v) => {
    const el = view.querySelector(`#${id}`);
    if (el && el.textContent !== String(v)) el.textContent = String(v);
  };
  const setNetSub = (online, total) => {
    const el = view.querySelector(".croc-cc-net-widget__s");
    if (!el) return;
    const next = fmtDevicesOnlineOfTotal(online, total);
    if (el.textContent !== next) el.textContent = next;
  };
  const total = vals.devices != null ? vals.devices : null;
  const online = vals.online != null ? vals.online : null;
  if (total != null) {
    set("ccFilterAll", total);
    set("ccStatTotal", total);
  }
  if (online != null) {
    set("ccFilterOnline", online);
    set("ccStatOnline", online);
  }
  if (vals.offline != null) set("ccFilterOffline", vals.offline);
  if (vals.incidents != null) set("ccStatIncidents", vals.incidents);
  if (total != null && online != null) setNetSub(online, total);
  let health = vals.health;
  if (health == null && total != null && Number(total) > 0 && online != null) {
    health = Math.round((100 * Number(online)) / Number(total));
  }
  // Top pill: prefer "N of M" (operator language) over bare %.
  if (online != null && total != null) {
    set("ccSystemHealth", `${online} of ${total}`);
    set("ccNetHealth", `${online} of ${total}`);
  } else if (online != null) {
    set("ccSystemHealth", String(online));
    set("ccNetHealth", String(online));
  } else if (health != null && Number.isFinite(Number(health))) {
    set("ccSystemHealth", `${Math.round(Number(health))}%`);
    set("ccNetHealth", `${Math.round(Number(health))}%`);
  } else {
    // Do not clobber a good initial SSR value with "—" when patch lacks counts.
    const cur = view.querySelector("#ccSystemHealth");
    if (cur && (!cur.textContent || cur.textContent.trim() === "—")) {
      set("ccSystemHealth", "—");
      set("ccNetHealth", "—");
    }
  }
  if (health != null && Number.isFinite(Number(health))) {
    set("ccStatHealth", `${Math.round(Number(health))}%`);
  } else {
    set("ccStatHealth", "—");
  }
}

/**
 * Patch live incident feed panels without full shell remount.
 * @param {ParentNode} view
 * @param {{ incidentCards?: object[], responders?: object[], timelineItems?: object[], aiPack?: object|null }} feed
 */
export function patchOverviewIncidentFeed(view, feed = {}) {
  const incidentCards = Array.isArray(feed.incidentCards) ? feed.incidentCards : [];
  const responders = Array.isArray(feed.responders) ? feed.responders : [];
  const timelineItems = Array.isArray(feed.timelineItems) ? feed.timelineItems : [];
  try { view.__ccIncidentCards = incidentCards; } catch (_) {}
  const feedEl = view.querySelector(".croc-cc-feed");
  if (feedEl) feedEl.innerHTML = buildIncidentCards(incidentCards);
  const liveCount = view.querySelector("#ccFeedLiveCount");
  if (liveCount) liveCount.textContent = String(incidentCards.length);
  const alertEl = view.querySelector("#ccHomeAlerts");
  if (alertEl) {
    const wb = view.querySelector("[data-home-wb]");
    const canAssign = String((wb && wb.getAttribute("data-home-role")) || "admin") !== "user";
    alertEl.outerHTML = buildHomeAlertPickHtml(incidentCards, { canAssign });
  }
  const respEl = view.querySelector("#ccHomeQueue, .croc-cc-workbench .croc-cc-responders, .croc-cc-responders");
  if (respEl) respEl.innerHTML = buildResponderRows(responders);
  const timelineEl = view.querySelector(".croc-cc-workbench .croc-cc-timeline, .croc-cc-timeline");
  if (timelineEl) timelineEl.innerHTML = buildTimeline(timelineItems);
  const keepAi = feed.aiPack === undefined
    ? (view.__ccAiPack || null)
    : feed.aiPack;
  if (feed.aiPack !== undefined) view.__ccAiPack = feed.aiPack;
  try {
    patchMapAiAlertStage(view, keepAi, incidentCards);
  } catch (_) {}
  const aiHost = view.querySelector(".croc-cc-workbench__ai");
  if (aiHost) {
    aiHost.innerHTML = buildAutoSentinelHomeHtml(incidentCards, keepAi);
    const maicLive = maicFieldsFromAiPack(keepAi);
    const waitingN = maicLive.waitingN;
    const packPulse = String((keepAi && keepAi.pulse) || (keepAi && keepAi.tonight && keepAi.tonight.pulse) || "").toLowerCase();
    const acting = waitingN <= 0 && (packPulse === "acting" || !!(keepAi && keepAi.tonight && keepAi.tonight.acting));
    const pulseKind = waitingN > 0 ? "interrupt" : acting ? "acting" : "watching";
    const pulse = view.querySelector("[data-home-wb] [data-as-pulse]");
    const lineTxt = waitingN > 0
      ? (waitingN === 1 ? "1 Needs You — Hold / Takeover or open Mission Room" : `${waitingN} Needs You — Hold / Takeover or open Mission Room`)
      : acting
        ? "The Watch is acting."
        : "Sentinel is watching — finish assign and field steps here when an alarm lands";
    const line = view.querySelector("#ccHomePulseLine");
    const lineCompact = view.querySelector("#ccHomePulseLineCompact");
    if (pulse) pulse.setAttribute("data-as-pulse", pulseKind);
    if (line) line.textContent = lineTxt;
    if (lineCompact) lineCompact.textContent = lineTxt;
    pulse?.querySelectorAll(".croc-as-pulse__stage").forEach((el, i) => {
      const lit = pulseKind === "interrupt" ? i === 2 : pulseKind === "acting" ? i === 1 : i === 0;
      el.classList.toggle("is-lit", lit);
    });
    const wf = view.querySelector(".croc-ios-wf");
    if (wf) {
      wf.querySelectorAll(".croc-ios-wf__step").forEach((el) => {
        const n = el.querySelector(".croc-ios-wf__n");
        const label = n ? String(n.textContent || "") : "";
        const want =
          (waitingN > 0 && /^4\s*·\s*Decide/i.test(label))
          || (waitingN <= 0 && /^2\s*·\s*Observe/i.test(label));
        el.classList.toggle("is-active", want);
        if (want) el.setAttribute("aria-current", "step");
        else el.removeAttribute("aria-current");
      });
    }
  }
  const wbRole = view.querySelector("[data-home-wb]");
  const isAdminHome = String((wbRole && wbRole.getAttribute("data-home-role")) || "admin") !== "user";
  const tonight = view.querySelector("[data-tonight]");
  if (tonight) {
    tonight.outerHTML = buildTonightWatchHtml(keepAi, { isAdmin: isAdminHome });
  }
  const aiCtx = view.querySelector("[data-ai-context]");
  if (aiCtx) {
    aiCtx.outerHTML = buildAiContextDeepHtml(keepAi, { isAdmin: isAdminHome });
  }
  /* Soft Soft map chip — mirrors /ai/soft-lanes without covering the map canvas */
  paintSoftLanesMapChip(view, keepAi && keepAi.softLanes ? keepAi.softLanes : null);
  patchOverviewCommandCenterMetrics(view, { incidents: incidentCards.length });
}

/**
 * Paint Soft Soft product chip into map toolbar host (map stays large).
 * @param {ParentNode | null | undefined} view
 * @param {object|null|undefined} softLanes
 */
export function paintSoftLanesMapChip(view, softLanes) {
  if (!view || !view.querySelector) return;
  const host = view.querySelector("#ccSoftSoftMapChip") || view.querySelector("[data-soft-mapchip-host]");
  if (!host) return;
  try {
    setChildMarkup(host, softLanesMapChipHtml(softLanes));
  } catch (_) {
    try {
      host.innerHTML = softLanesMapChipHtml(softLanes);
    } catch (_) {}
  }
}
